import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useJWTAuth } from "../context/JWTAuthContext";
import travelApiService from "../utils/travelApiService";
import { convertObjectBase64Images } from "../utils/imageUploadHelper";
import RichTextEditor from "../components/RichTextEditor";
import ShareLinkModal from "../components/ShareLinkModal";
import ShareLinkList from "../components/ShareLinkList";
import "./TravelDetailPage.css";

const TravelDetailPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useJWTAuth();
  const [travelPlan, setTravelPlan] = useState(null);
  const [selectedDay, setSelectedDay] = useState(0);
  const [dayStartTime, setDayStartTime] = useState("09:00");
  const [editingActivity, setEditingActivity] = useState(null);
  const [isEditingStartTime, setIsEditingStartTime] = useState(false);
  const [showAddActivityForm, setShowAddActivityForm] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // 서버 저장 중 상태
  const [isRefreshing, setIsRefreshing] = useState(false); // 서버 새로고침 중 상태

  // 드래그앤드롭 상태 - useRef로 관리하여 리렌더링 방지
  const draggedIndexRef = useRef(null);
  const draggedElementRef = useRef(null);
  const insertPositionRef = useRef(null);
  const dropIndicatorRef = useRef(null);

  // 사용 가능한 아이콘 목록
  const availableIcons = [
    '👤', '👥', '👨', '👩', '🧑', '👶', '👧', '👦', '🧒',
    '👨‍🦰', '👩‍🦰', '👨‍🦱', '👩‍🦱', '👨‍🦲', '👩‍🦲', '👨‍🦳', '👩‍🦳',
    '🧔', '🧔‍♂️', '🧔‍♀️', '👱', '👱‍♂️', '👱‍♀️'
  ];

  // 참여자 관리 상태
  const [showParticipantManagement, setShowParticipantManagement] =
    useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [newParticipant, setNewParticipant] = useState({
    name: "",
    contact: "",
    icon: "👤",
  });

  // 여행 정보 더보기 모달
  const [showTravelInfoModal, setShowTravelInfoModal] = useState(false);
  const [isEditingTravelInfo, setIsEditingTravelInfo] = useState(false);
  const [editedTravelInfo, setEditedTravelInfo] = useState({
    title: "",
    destination: "",
    startDate: "",
    endDate: "",
    budget: "",
    description: "",
  });

  // 청구 목록 모달
  const [showBillingModal, setShowBillingModal] = useState(false);

  // 현재 보고 있는 페이지 타입 ('info' | 'booking' | 'checklist' | 'shared')
  const [currentPageType, setCurrentPageType] = useState('info');

  // 공유 링크 모달
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareListModal, setShowShareListModal] = useState(false);

  // 더보기 메뉴 표시 상태
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // 예약 정보 상태
  const [transportations, setTransportations] = useState([]); // 교통 정보 배열
  const [accommodations, setAccommodations] = useState([]); // 숙박 정보 배열
  const [isEditingBooking, setIsEditingBooking] = useState(false);
  const [showAddTransport, setShowAddTransport] = useState(false);
  const [showAddAccommodation, setShowAddAccommodation] = useState(false);
  const [newTransport, setNewTransport] = useState({
    type: '',
    reservationNumber: '',
    budget: '',
    people: [], // 참여자 배열
    memo: ''
  });
  const [newAccommodation, setNewAccommodation] = useState({
    name: '',
    address: '',
    reservationNumber: '',
    budget: '',
    people: [], // 참여자 배열
    memo: ''
  });

  // 예약 정보 수정 상태
  const [editingTransportId, setEditingTransportId] = useState(null);
  const [editingAccommodationId, setEditingAccommodationId] = useState(null);

  // 체크리스트 상태
  const [checklist, setChecklist] = useState([]); // 체크리스트 배열
  const [newChecklistItem, setNewChecklistItem] = useState("");
  const [isEditingChecklist, setIsEditingChecklist] = useState(false);

  // 시작 시간 편집 영역 참조
  const startTimeEditRef = useRef(null);

  // 더보기 메뉴 참조
  const moreMenuRef = useRef(null);

  // 이미지 URL을 프록시 경로로 변환하는 함수
  const convertImageUrlsForProxy = (html) => {
    if (!html) return html;
    // "http://localhost:포트번호/uploads/..." → "/travel-api/uploads/..."
    let converted = html.replace(
      /src="https?:\/\/localhost:\d+(\/uploads\/[^"]+)"/g,
      'src="/travel-api$1"'
    );
    // "/uploads/..." → "/travel-api/uploads/..." (상대 경로)
    converted = converted.replace(
      /src="(\/uploads\/[^"]+)"/g,
      'src="/travel-api$1"'
    );
    return converted;
  };

  // 두 시간 사이의 분 단위 차이 계산
  const calculateTimeDifference = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return endMinutes - startMinutes;
  };

  // 활동 목록에 자유시간 버퍼 삽입
  const insertBufferActivities = (activities) => {
    if (!activities || activities.length === 0) return [];

    const activitiesWithBuffers = [];

    for (let i = 0; i < activities.length; i++) {
      const current = activities[i];
      activitiesWithBuffers.push(current);

      const next = activities[i + 1];
      if (!next) continue;

      // 현재 활동의 종료 시간
      const currentEndTime = current.type === 'fix' ? current.endTime : current.endTime;

      // 다음 활동이 Fix 타입이고 시작 시간이 있을 때
      if (next.type === 'fix' && next.startTime && currentEndTime) {
        const gap = calculateTimeDifference(currentEndTime, next.startTime);

        // 시간 차이가 1분 이상일 때만 버퍼 생성
        if (gap > 0) {
          activitiesWithBuffers.push({
            id: `buffer-${current.id}-${next.id}`,
            type: 'buffer',
            title: '자유시간',
            startTime: currentEndTime,
            endTime: next.startTime,
            duration: gap,
            isBuffer: true,
            location: '',
            budget: 0,
            people: [],
            description: '',
            category: null
          });
        }
      }
    }

    return activitiesWithBuffers;
  };

  // 카테고리 관리 상태
  const [categories, setCategories] = useState([
    { id: "meal", name: "식사", color: "#ff9800" },
    { id: "transport", name: "이동", color: "#2196f3" },
    { id: "tour", name: "관광", color: "#4caf50" },
    { id: "shopping", name: "쇼핑", color: "#e91e63" },
    { id: "rest", name: "휴식", color: "#9c27b0" },
  ]);
  const [showCategoryManagement, setShowCategoryManagement] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState("#757575");

  const [newActivity, setNewActivity] = useState({
    time: "",
    duration: "",
    title: "",
    location: "",
    budget: "",
    people: [], // 선택된 참여자들의 배열
    description: "",
    category: null, // 카테고리 ID
  });

  // 카테고리 데이터 로드
  useEffect(() => {
    const savedCategories = localStorage.getItem("activityCategories");
    if (savedCategories) {
      setCategories(JSON.parse(savedCategories));
    }
  }, []);

  // 카테고리 저장
  const saveCategories = (updatedCategories) => {
    localStorage.setItem("activityCategories", JSON.stringify(updatedCategories));
    setCategories(updatedCategories);
  };

  // 카테고리 추가
  const handleAddCategory = () => {
    if (!newCategoryName.trim()) {
      alert("카테고리 이름을 입력하세요.");
      return;
    }
    const newCategory = {
      id: `category-${Date.now()}`,
      name: newCategoryName.trim(),
      color: newCategoryColor,
    };
    const updatedCategories = [...categories, newCategory];
    saveCategories(updatedCategories);
    setNewCategoryName("");
    setNewCategoryColor("#757575");
  };

  // 카테고리 삭제
  const handleDeleteCategory = (categoryId) => {
    if (window.confirm("이 카테고리를 삭제하시겠습니까?")) {
      const updatedCategories = categories.filter((c) => c.id !== categoryId);
      saveCategories(updatedCategories);
    }
  };

  // 카테고리 색상 변경
  const handleChangeCategoryColor = (categoryId, newColor) => {
    const updatedCategories = categories.map((c) =>
      c.id === categoryId ? { ...c, color: newColor } : c
    );
    saveCategories(updatedCategories);
  };

  // 청구 목록 계산 함수
  const calculateBillingList = React.useMemo(() => {
    if (!travelPlan || !travelPlan.days) return {};

    const billingMap = {}; // { participantId: { name, totalAmount, activities: [] } }

    // 모든 날짜의 모든 활동 순회
    travelPlan.days.forEach((day, dayIndex) => {
      if (!day.activities) return;

      day.activities.forEach((activity) => {
        const budget = parseInt(activity.budget) || 0;
        if (budget <= 0) return; // 예산이 0이면 스킵

        const participants = Array.isArray(activity.people) ? activity.people : [];

        if (participants.length > 0) {
          // 참여자가 있으면 예산을 참여자 수로 나눔
          const amountPerPerson = budget / participants.length;

          participants.forEach((participantId) => {
            const participant = travelPlan.participants?.find(
              (p) => p.id === participantId
            );
            const participantName = participant ? participant.name : "알 수 없음";

            if (!billingMap[participantId]) {
              billingMap[participantId] = {
                id: participantId,
                name: participantName,
                totalAmount: 0,
                activities: [],
              };
            }

            billingMap[participantId].totalAmount += amountPerPerson;
            billingMap[participantId].activities.push({
              day: dayIndex + 1,
              title: activity.title,
              budget: budget,
              sharedAmount: amountPerPerson,
              participantsCount: participants.length,
            });
          });
        } else {
          // 참여자가 없으면 'None'으로 표시
          const noneId = "none";
          if (!billingMap[noneId]) {
            billingMap[noneId] = {
              id: noneId,
              name: "None",
              totalAmount: 0,
              activities: [],
            };
          }

          billingMap[noneId].totalAmount += budget;
          billingMap[noneId].activities.push({
            day: dayIndex + 1,
            title: activity.title,
            budget: budget,
            sharedAmount: budget,
            participantsCount: 0,
          });
        }
      });
    });

    // 교통 정보 비용 추가 (state 변수 사용)
    if (transportations && transportations.length > 0) {
      transportations.forEach((transport) => {
        const budget = parseInt(transport.budget) || 0;
        if (budget <= 0) return;

        // participants 배열 (객체 배열) 또는 people 배열 (ID 배열) 지원
        let participantIds = [];
        if (transport.participants && Array.isArray(transport.participants)) {
          participantIds = transport.participants.map(p => p.id);
        } else if (transport.people && Array.isArray(transport.people)) {
          participantIds = transport.people;
        }
        const participants = participantIds;

        if (participants.length > 0) {
          const amountPerPerson = budget / participants.length;

          participants.forEach((participantId) => {
            const participant = travelPlan.participants?.find(
              (p) => p.id === participantId
            );
            const participantName = participant ? participant.name : "알 수 없음";

            if (!billingMap[participantId]) {
              billingMap[participantId] = {
                id: participantId,
                name: participantName,
                totalAmount: 0,
                activities: [],
              };
            }

            billingMap[participantId].totalAmount += amountPerPerson;
            billingMap[participantId].activities.push({
              day: null,
              title: `🚗 ${transport.type}`,
              budget: budget,
              sharedAmount: amountPerPerson,
              participantsCount: participants.length,
            });
          });
        } else {
          const noneId = "none";
          if (!billingMap[noneId]) {
            billingMap[noneId] = {
              id: noneId,
              name: "None",
              totalAmount: 0,
              activities: [],
            };
          }

          billingMap[noneId].totalAmount += budget;
          billingMap[noneId].activities.push({
            day: null,
            title: `🚗 ${transport.type}`,
            budget: budget,
            sharedAmount: budget,
            participantsCount: 0,
          });
        }
      });
    }

    // 숙박 정보 비용 추가 (state 변수 사용)
    if (accommodations && accommodations.length > 0) {
      accommodations.forEach((accommodation) => {
        const budget = parseInt(accommodation.budget) || 0;
        if (budget <= 0) return;

        // participants 배열 (객체 배열) 또는 people 배열 (ID 배열) 지원
        let participantIds = [];
        if (accommodation.participants && Array.isArray(accommodation.participants)) {
          participantIds = accommodation.participants.map(p => p.id);
        } else if (accommodation.people && Array.isArray(accommodation.people)) {
          participantIds = accommodation.people;
        }
        const participants = participantIds;

        if (participants.length > 0) {
          const amountPerPerson = budget / participants.length;

          participants.forEach((participantId) => {
            const participant = travelPlan.participants?.find(
              (p) => p.id === participantId
            );
            const participantName = participant ? participant.name : "알 수 없음";

            if (!billingMap[participantId]) {
              billingMap[participantId] = {
                id: participantId,
                name: participantName,
                totalAmount: 0,
                activities: [],
              };
            }

            billingMap[participantId].totalAmount += amountPerPerson;
            billingMap[participantId].activities.push({
              day: null,
              title: `🏨 ${accommodation.name}`,
              budget: budget,
              sharedAmount: amountPerPerson,
              participantsCount: participants.length,
            });
          });
        } else {
          const noneId = "none";
          if (!billingMap[noneId]) {
            billingMap[noneId] = {
              id: noneId,
              name: "None",
              totalAmount: 0,
              activities: [],
            };
          }

          billingMap[noneId].totalAmount += budget;
          billingMap[noneId].activities.push({
            day: null,
            title: `🏨 ${accommodation.name}`,
            budget: budget,
            sharedAmount: budget,
            participantsCount: 0,
          });
        }
      });
    }

    return billingMap;
  }, [travelPlan, transportations, accommodations]);

  // 여행 계획 데이터 로드
  useEffect(() => {
    const loadTravelPlan = async () => {
      // 1. 먼저 localStorage에서 확인
      const savedPlans = localStorage.getItem("travelPlans");
      let plan = null;

      if (savedPlans) {
        const plans = JSON.parse(savedPlans);
        plan = plans.find((p) => String(p.id) === String(planId));
      }

      // 2. 로컬 ID가 아니고 로그인 상태이면 서버에서 최신 정보 가져오기
      const isLocalId = typeof planId === 'string' && planId.startsWith('local-');

      if (!isLocalId && isAuthenticated) {
        try {
          console.log('🔄 [TravelDetailPage] 서버에서 여행 계획 불러오는 중... ID:', planId);
          const response = await travelApiService.getTravelPlan(planId);

          if (response.success && response.data) {
            const serverPlan = response.data;
            console.log('✅ [TravelDetailPage] 서버에서 불러온 여행 계획:', serverPlan);

            // 서버 데이터를 프론트엔드 형식으로 변환
            const convertDate = (isoDate) => {
              if (!isoDate) return '';
              return isoDate.split('T')[0];
            };

            // activities의 시간 변환 및 재계산
            // 서버에서 받은 days를 날짜 순서대로 정렬
            const sortedDays = (serverPlan.days || []).sort((a, b) => {
              const dateA = new Date(a.date || a.day_date);
              const dateB = new Date(b.date || b.day_date);
              return dateA - dateB;
            });

            const convertedDays = sortedDays.map(day => {
              const activities = (day.activities || [])
                .map(activity => {
                  // participants 배열을 people ID 배열로 변환
                  const people = activity.participants
                    ? activity.participants.map(p => p.id)
                    : [];

                  return {
                    ...activity,
                    type: activity.type || 'duration', // 타입 기본값
                    startTime: normalizeTime(activity.start_time || activity.startTime),
                    endTime: normalizeTime(activity.end_time || activity.endTime),
                    duration: activity.duration || 0,
                    people: people, // ID 배열로 변환
                  };
                })
                .sort((a, b) => {
                  // activity_order 또는 id로 정렬
                  const orderA = a.activity_order !== undefined ? a.activity_order : a.id;
                  const orderB = b.activity_order !== undefined ? b.activity_order : b.id;
                  return orderA - orderB;
                });

              // 해당 날짜의 시작 시간 (서버 응답의 start_time을 "HH:MM:SS" → "HH:MM"으로 변환)
              const dayStartTime = normalizeTime(day.start_time) || day.startTime || "09:00";

              // activities가 있으면 시작 시간 기준으로 재계산
              let currentTime = dayStartTime;
              const recalculatedActivities = activities.map((activity, index) => {
                // fix 타입은 고정된 시각이므로 재계산하지 않음
                if (activity.type === 'fix') {
                  if (activity.endTime) {
                    currentTime = activity.endTime;
                  }
                  return activity;
                }

                // duration 타입만 재계산
                let activityStartTime;
                if (index === 0) {
                  // 첫 번째는 startTime이 null이면 dayStartTime 사용
                  activityStartTime = activity.startTime || currentTime;
                } else {
                  // 두 번째 이후는 이전 activity의 endTime 사용
                  activityStartTime = currentTime;
                }

                const endTime = addMinutes(activityStartTime, activity.duration);
                currentTime = endTime;

                return {
                  ...activity,
                  startTime: activityStartTime,
                  endTime: endTime,
                };
              });

              return {
                ...day,
                startTime: dayStartTime,
                activities: recalculatedActivities,
              };
            });

            plan = {
              id: serverPlan.id,
              title: serverPlan.title,
              destination: serverPlan.destination,
              startDate: convertDate(serverPlan.start_date),
              endDate: convertDate(serverPlan.end_date),
              duration: serverPlan.duration,
              budget: serverPlan.budget,
              description: serverPlan.description,
              createdAt: serverPlan.created_at,
              participants: serverPlan.participants || [],
              days: convertedDays,
              transportations: serverPlan.transportations || [],
              accommodations: serverPlan.accommodations || [],
              checklist: serverPlan.checklist || [],
              is_owner: serverPlan.is_owner,
              permission: serverPlan.permission,
            };

            // localStorage 업데이트 (캐싱)
            if (savedPlans) {
              const plans = JSON.parse(savedPlans);
              const existingIndex = plans.findIndex((p) => String(p.id) === String(planId));

              if (existingIndex >= 0) {
                plans[existingIndex] = plan;
              } else {
                plans.push(plan);
              }

              localStorage.setItem("travelPlans", JSON.stringify(plans));
            } else {
              localStorage.setItem("travelPlans", JSON.stringify([plan]));
            }

            console.log('✅ [TravelDetailPage] localStorage 업데이트 완료');
          } else {
            throw new Error(response.message || '여행 계획을 불러올 수 없습니다.');
          }
        } catch (error) {
          console.error('❌ [TravelDetailPage] 서버에서 불러오기 실패:', error);
          // 서버 실패 시 localStorage의 데이터 사용
          if (!plan) {
            alert('서버에서 데이터를 불러올 수 없습니다. 오프라인 데이터를 사용합니다.');
          }
        }
      }

      if (plan) {
        // days 배열이 없거나 비어있으면 자동 생성
        if (!plan.days || plan.days.length === 0) {
          console.log('⚠️ [TravelDetailPage] days 배열이 없음, 자동 생성');

          // startDate, endDate, duration 정보로 days 배열 생성
          if (plan.startDate && plan.duration) {
            const startDate = new Date(plan.startDate);
            plan.days = Array.from({ length: plan.duration }, (_, index) => ({
              date: new Date(startDate.getTime() + index * 24 * 60 * 60 * 1000)
                .toISOString()
                .split('T')[0],
              activities: []
            }));
            console.log('✅ [TravelDetailPage] days 배열 생성 완료:', plan.days.length, '일');
          } else {
            console.error('❌ [TravelDetailPage] startDate 또는 duration 정보 없음');
            plan.days = []; // 빈 배열로 초기화
          }
        }

        // 참여자 목록이 없거나 비어있으면 '나' 참여자 자동 추가
        if (!plan.participants || plan.participants.length === 0) {
          const meParticipant = {
            id: `me-${Date.now()}`,
            name: "나",
            contact: "",
            icon: "👤",
          };
          plan.participants = [meParticipant];
        }

        // 변경사항이 있으면 localStorage 업데이트
        if (savedPlans) {
          const plans = JSON.parse(savedPlans);
          const updatedPlans = plans.map((p) =>
            String(p.id) === String(planId) ? plan : p
          );
          localStorage.setItem("travelPlans", JSON.stringify(updatedPlans));
        } else {
          // localStorage에 아무것도 없으면 새로 생성
          localStorage.setItem("travelPlans", JSON.stringify([plan]));
        }

        setTravelPlan(plan);

        // 예약 정보 로드
        if (plan.transportations) {
          setTransportations(plan.transportations);
        }
        if (plan.accommodations) {
          setAccommodations(plan.accommodations);
        }

        // 체크리스트 로드
        if (plan.checklist) {
          setChecklist(plan.checklist);
        }

        // 첫 번째 날짜(selectedDay=0)의 저장된 시작 시간 로드
        const dayData = plan.days && plan.days[selectedDay];
        const initialStartTime =
          dayData && dayData.startTime ? dayData.startTime : "09:00";
        setDayStartTime(initialStartTime);
      } else {
        navigate("/travel");
      }
    };

    // async 함수 실행
    loadTravelPlan();
  }, [planId, navigate, selectedDay, isAuthenticated]);

  // 선택된 날짜가 변경될 때 해당 날짜의 시작 시간 로드
  useEffect(() => {
    if (travelPlan && travelPlan.days && travelPlan.days[selectedDay]) {
      const dayData = travelPlan.days[selectedDay];

      // 각 날짜별로 저장된 시작 시간이 있으면 로드, 없으면 기본값
      const savedStartTime = dayData.startTime || "09:00";
      setDayStartTime(savedStartTime);
    }
  }, [selectedDay, travelPlan]);

  // 예약 정보 페이지 열 때 서버에서 최신 데이터 조회
  useEffect(() => {
    const loadBookingData = async () => {
      if (currentPageType !== 'booking' || !showTravelInfoModal) {
        return;
      }

      const isServerPlan = typeof travelPlan?.id === 'number' || (typeof travelPlan?.id === 'string' && !travelPlan.id.startsWith('local-'));
      if (!isAuthenticated || !isServerPlan) {
        return;
      }

      try {
        console.log('🔄 [예약 정보] 서버에서 최신 데이터 조회 중...');

        // 교통 정보 조회
        const transportResponse = await travelApiService.getTransportations(travelPlan.id);
        if (transportResponse.success && transportResponse.data) {
          console.log('✅ [교통 정보] 서버 조회 성공:', transportResponse.data.length, '개');

          // 서버 데이터 처리 시 participants 중복 제거
          const cleanedTransportations = transportResponse.data.map(transport => ({
            ...transport,
            participants: transport.participants
              ? Array.from(new Map(transport.participants.map(p => [p.id, p])).values())
              : []
          }));

          setTransportations(cleanedTransportations);

          // localStorage 업데이트
          const updatedPlan = {
            ...travelPlan,
            transportations: cleanedTransportations
          };
          saveTravelPlan(updatedPlan);
        }

        // 숙박 정보 조회
        const accommodationResponse = await travelApiService.getAccommodations(travelPlan.id);
        if (accommodationResponse.success && accommodationResponse.data) {
          console.log('✅ [숙박 정보] 서버 조회 성공:', accommodationResponse.data.length, '개');

          // 서버 데이터 처리 시 participants 중복 제거
          const cleanedAccommodations = accommodationResponse.data.map(accommodation => ({
            ...accommodation,
            participants: accommodation.participants
              ? Array.from(new Map(accommodation.participants.map(p => [p.id, p])).values())
              : []
          }));

          setAccommodations(cleanedAccommodations);

          // localStorage 업데이트
          const updatedPlan = {
            ...travelPlan,
            accommodations: cleanedAccommodations
          };
          saveTravelPlan(updatedPlan);
        }
      } catch (error) {
        console.error('❌ [예약 정보] 서버 조회 실패:', error);
      }
    };

    loadBookingData();
  }, [currentPageType, showTravelInfoModal, travelPlan?.id, isAuthenticated]);

  // 더보기 메뉴 외부 클릭 감지
  useEffect(() => {
    if (!showMoreMenu) return;

    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu]);

  // 여행 계획 저장 - localStorage에 각 날짜별 시작 시간 포함하여 저장
  const saveTravelPlan = (updatedPlan, originalPlanId = null) => {
    const savedPlans = localStorage.getItem("travelPlans");
    if (savedPlans) {
      const plans = JSON.parse(savedPlans);
      const idToMatch = originalPlanId || planId; // 서버 저장 시 로컬 ID를 전달받음
      // ID를 문자열로 변환 후 비교 (타입 불일치 방지)
      const updatedPlans = plans.map((p) =>
        String(p.id) === String(idToMatch) || String(p.id) === String(updatedPlan.id) ? updatedPlan : p
      );
      localStorage.setItem("travelPlans", JSON.stringify(updatedPlans));
      setTravelPlan(updatedPlan);
    }
  };

  // React-Time-Picker에서는 문자열 형태로 직접 처리

  // 시간 추가 계산 (분 단위)
  const addMinutes = (baseTime, minutes) => {
    if (!baseTime || minutes === null || minutes === undefined) return "";

    const [baseHours, baseMinutes] = baseTime.split(":").map(Number);
    const baseDate = new Date();
    baseDate.setHours(baseHours, baseMinutes, 0, 0);

    const durationMs = minutes * 60 * 1000;
    const resultDate = new Date(baseDate.getTime() + durationMs);

    return resultDate.toTimeString().slice(0, 5);
  };

  // 시간 형식 검증 및 정규화 (백엔드용 - "HH:MM" 형식 유지)
  const normalizeTime = (timeStr) => {
    if (!timeStr) return null;

    // 이미 "HH:MM" 형식이면 그대로 반환
    if (typeof timeStr === 'string' && timeStr.match(/^\d{2}:\d{2}$/)) {
      return timeStr;
    }

    // "HH:MM:SS" 형식이면 "HH:MM"으로 변환 (서버 응답 형식)
    if (typeof timeStr === 'string' && timeStr.match(/^\d{2}:\d{2}:\d{2}$/)) {
      return timeStr.substring(0, 5); // "HH:MM:SS" -> "HH:MM"
    }

    // ISO datetime 형식이면 "HH:MM"으로 변환
    if (typeof timeStr === 'string' && timeStr.includes('T')) {
      const date = new Date(timeStr);
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    }

    return timeStr;
  };

  // 다음 활동 시작 시간 계산
  const getNextStartTime = (activities, dayStartTime) => {
    if (!activities || activities.length === 0) {
      return dayStartTime;
    }

    const lastActivity = activities[activities.length - 1];
    return lastActivity.endTime || dayStartTime;
  };

  // 활동들의 시간을 순차적으로 재계산
  const recalculateActivityTimes = (activities, startTime, previousStartTime = null) => {
    let currentTime = startTime;
    return activities.map((activity, index) => {
      // fix 타입은 고정된 시각이므로 재계산하지 않음
      if (activity.type === 'fix') {
        // fix 타입은 다음 활동의 currentTime만 업데이트하고 그대로 반환
        if (activity.endTime) {
          currentTime = activity.endTime;
        }
        return activity;
      }

      // duration 타입만 재계산
      // 첫 번째 activity이고 startTime이 없거나 이전 dayStartTime과 같으면 새 dayStartTime 사용
      // 아니면 activity의 startTime 사용 (명시적으로 설정된 시간)
      let activityStartTime;
      if (index === 0) {
        // 첫 번째는 startTime이 없거나, 이전 dayStartTime과 같았으면 현재 dayStartTime 적용
        const wasUsingPreviousStartTime = !activity.startTime ||
          (previousStartTime && activity.startTime === previousStartTime);

        activityStartTime = wasUsingPreviousStartTime
          ? currentTime
          : activity.startTime;
      } else {
        // 두 번째 이후는 이전 activity의 endTime 사용
        activityStartTime = currentTime;
      }

      const updatedActivity = {
        ...activity,
        startTime: activityStartTime,
        endTime: addMinutes(activityStartTime, activity.duration),
      };
      currentTime = updatedActivity.endTime;
      return updatedActivity;
    });
  };

  // 인라인 활동 저장
  const handleSaveInlineActivity = React.useCallback(async (
    activityData,
    isEdit = false,
    activityId = null
  ) => {
    if (!travelPlan.days || !travelPlan.days[selectedDay]) {
      console.error('❌ [handleSaveInlineActivity] days 배열 또는 선택된 day가 없음');
      return;
    }

    const updatedPlan = { ...travelPlan };
    const dayActivities = [...(updatedPlan.days[selectedDay].activities || [])];

    // dayId 계산 (백엔드는 1부터 시작)
    const dayId = selectedDay + 1;
    const isServerPlan = typeof travelPlan.id === 'number' || (typeof travelPlan.id === 'string' && !travelPlan.id.startsWith('local-'));

    if (isEdit && activityId) {
      // 수정
      const activityIndex = dayActivities.findIndex((a) => a.id === activityId);
      if (activityIndex !== -1) {
        // fix 타입인 경우 fixStartTime/fixEndTime을 startTime/endTime으로 변환
        const updatedActivityData = { ...activityData };
        if (activityData.type === 'fix') {
          updatedActivityData.startTime = activityData.fixStartTime || activityData.startTime;
          updatedActivityData.endTime = activityData.fixEndTime || activityData.endTime;
        }

        dayActivities[activityIndex] = {
          ...dayActivities[activityIndex],
          ...updatedActivityData,
        };

        // 서버에 저장
        if (isServerPlan && isAuthenticated) {
          const isServerActivityId = typeof activityId === 'number' || (typeof activityId === 'string' && !activityId.startsWith('local-'));

          if (isServerActivityId) {
            try {
              const activityType = activityData.type || 'duration';
              const apiData = {
                type: activityType,
                title: activityData.title,
                location: activityData.location || '',
                budget: parseInt(activityData.budget) || 0,
                people: activityData.people || [],
                description: activityData.description || '',
                category: activityData.category || null,
              };

              if (activityType === 'fix') {
                // fix 타입: 시작/종료 시각 필수
                apiData.startTime = normalizeTime(activityData.fixStartTime || activityData.startTime);
                apiData.endTime = normalizeTime(activityData.fixEndTime || activityData.endTime);
              } else {
                // duration 타입: 소요시간 필수
                apiData.duration = parseInt(activityData.duration) || 0;
                // startTime이 명시적으로 있고 dayStartTime과 다를 때만 포함
                if (activityData.startTime && activityData.startTime !== dayStartTime) {
                  apiData.startTime = normalizeTime(activityData.startTime);
                }
              }

              await travelApiService.updateActivity(travelPlan.id, dayId, activityId, apiData);
              console.log('✅ [활동 수정] 서버 업데이트 성공');
            } catch (error) {
              console.error('❌ [활동 수정] 서버 업데이트 실패:', error);
            }
          }
        }
      }
    } else {
      // 새로 추가
      const localActivityId = `local-${Date.now()}`;

      // fix 타입인 경우 fixStartTime/fixEndTime을 startTime/endTime으로 변환
      let finalStartTime = activityData.startTime;
      let finalEndTime = activityData.endTime;
      if (activityData.type === 'fix') {
        finalStartTime = activityData.fixStartTime || activityData.startTime;
        finalEndTime = activityData.fixEndTime || activityData.endTime;
      }

      const newActivityData = {
        id: localActivityId,
        type: activityData.type || 'duration',
        startTime: finalStartTime,
        endTime: finalEndTime,
        duration: activityData.duration,
        title: activityData.title || "새 활동",
        location: activityData.location || "",
        budget: parseInt(activityData.budget) || 0,
        people: Array.isArray(activityData.people)
          ? activityData.people
          : activityData.people
          ? [activityData.people]
          : [],
        description: activityData.description || "",
        category: activityData.category || null,
        createdAt: new Date().toISOString(),
      };
      dayActivities.push(newActivityData);

      // 서버에 저장
      if (isServerPlan && isAuthenticated) {
        try {
          const activityType = activityData.type || 'duration';
          const apiData = {
            type: activityType,
            title: newActivityData.title,
            location: newActivityData.location || '',
            budget: parseInt(newActivityData.budget) || 0,
            people: newActivityData.people || [],
            description: newActivityData.description || '',
            category: newActivityData.category || '',
          };

          if (activityType === 'fix') {
            // fix 타입: 시작/종료 시각 필수
            apiData.startTime = normalizeTime(activityData.fixStartTime || newActivityData.startTime);
            apiData.endTime = normalizeTime(activityData.fixEndTime || newActivityData.endTime);
          } else {
            // duration 타입: 소요시간 필수
            apiData.duration = parseInt(newActivityData.duration) || 0;
            // startTime이 명시적으로 있을 때만 포함
            if (newActivityData.startTime && newActivityData.startTime !== dayStartTime) {
              apiData.startTime = normalizeTime(newActivityData.startTime);
            }
          }

          const response = await travelApiService.createActivity(travelPlan.id, dayId, apiData);

          // 서버 ID로 교체 및 서버 응답 데이터 반영
          if (response.success && response.data && response.data.id) {
            newActivityData.id = response.data.id;
            // 서버가 계산한 startTime과 endTime 반영 ("HH:MM:SS" → "HH:MM")
            if (response.data.start_time) {
              newActivityData.startTime = normalizeTime(response.data.start_time);
            }
            if (response.data.end_time) {
              newActivityData.endTime = normalizeTime(response.data.end_time);
            }
            console.log('✅ [활동 추가] 서버 생성 성공, id:', response.data.id);
          }
        } catch (error) {
          console.error('❌ [활동 추가] 서버 생성 실패:', error);
        }
      }
    }

    // 시간순 정렬
    dayActivities.sort((a, b) => a.startTime.localeCompare(b.startTime));

    // 모든 활동의 시간을 순차적으로 재계산
    const recalculatedActivities = recalculateActivityTimes(
      dayActivities,
      dayStartTime
    );

    updatedPlan.days[selectedDay].activities = recalculatedActivities;
    saveTravelPlan(updatedPlan);
  }, [travelPlan, selectedDay, dayStartTime, isAuthenticated]);

  // 새 활동 추가 폼 표시
  const handleShowAddActivity = () => {
    setShowAddActivityForm(true);
  };

  // 새 활동 추가
  const handleAddActivity = (activityData) => {
    if (!travelPlan.days || !travelPlan.days[selectedDay]) {
      console.error('❌ [TravelDetailPage] days 배열 또는 선택된 day가 없음');
      return;
    }
    const currentActivities = travelPlan.days[selectedDay].activities || [];
    const startTime = getNextStartTime(currentActivities, dayStartTime);
    const duration = parseInt(activityData?.duration) || 0;
    const endTime = addMinutes(startTime, duration);

    const newActivityData = {
      startTime: startTime,
      endTime: endTime,
      duration: duration,
      title: activityData?.title || "",
      location: activityData?.location || "",
      budget: parseInt(activityData?.budget) || 0,
      people: Array.isArray(activityData?.people)
        ? activityData.people
        : activityData?.people
        ? [activityData.people]
        : [],
      description: activityData?.description || "",
    };

    handleSaveInlineActivity(newActivityData, false);
    setShowAddActivityForm(false);
  };

  // 참여자 추가
  const handleAddParticipant = () => {
    if (!newParticipant.name.trim()) {
      alert("참여자 이름은 필수입니다.");
      return;
    }

    const participantId = Date.now().toString();
    const participant = {
      id: participantId,
      name: newParticipant.name.trim(),
      contact: newParticipant.contact.trim(),
      icon: newParticipant.icon || "👤",
    };

    const updatedPlan = { ...travelPlan };
    if (!updatedPlan.participants) {
      updatedPlan.participants = [];
    }
    updatedPlan.participants.push(participant);

    saveTravelPlan(updatedPlan);

    // 폼 초기화
    setNewParticipant({ name: "", contact: "", icon: "👤" });
    setShowAddParticipant(false);
  };

  // 참여자 삭제
  const handleRemoveParticipant = (participantId) => {
    if (window.confirm("이 참여자를 삭제하시겠습니까?")) {
      const updatedPlan = { ...travelPlan };
      if (updatedPlan.participants) {
        updatedPlan.participants = updatedPlan.participants.filter(
          (p) => p.id !== participantId
        );
      }
      saveTravelPlan(updatedPlan);
    }
  };

  // 시작 시간 저장 - 각 날짜별로 독립적으로 localStorage에 저장
  const handleSaveStartTime = async () => {
    setIsEditingStartTime(false);

    // 여행 계획 업데이트 (선택된 날짜의 시작 시간 저장)
    const updatedPlan = { ...travelPlan };
    if (!updatedPlan.days[selectedDay]) {
      updatedPlan.days[selectedDay] = { activities: [] };
    }

    // 이전 시작 시간 저장 (비교용)
    const previousStartTime = updatedPlan.days[selectedDay].startTime || "09:00";

    // 선택된 날짜에 새 시작 시간 저장
    updatedPlan.days[selectedDay].startTime = dayStartTime;

    // 기존 활동들이 있다면 새로운 시작 시간으로 모든 활동 시간 재계산
    const currentActivities = travelPlan.days[selectedDay].activities || [];
    if (currentActivities.length > 0) {
      const recalculatedActivities = recalculateActivityTimes(
        currentActivities,
        dayStartTime,
        previousStartTime  // 이전 시작 시간 전달
      );
      updatedPlan.days[selectedDay].activities = recalculatedActivities;
    }

    // 서버에 저장 (서버 플랜이고 로그인된 경우)
    const isServerPlan = typeof travelPlan.id === 'number' ||
      (typeof travelPlan.id === 'string' && !travelPlan.id.startsWith('local-'));

    if (isServerPlan && isAuthenticated) {
      try {
        const dayId = selectedDay + 1; // 서버는 1부터 시작
        await travelApiService.updateDayStartTime(travelPlan.id, dayId, dayStartTime);
        console.log('✅ [Day 시작 시간] 서버 업데이트 성공:', dayStartTime);
      } catch (error) {
        console.error('❌ [Day 시작 시간] 서버 업데이트 실패:', error);
      }
    }

    // localStorage에 저장
    saveTravelPlan(updatedPlan);

    // state 업데이트하여 화면 리렌더링
    setTravelPlan(updatedPlan);
  };

  // 시작 시간 편집 중 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isEditingStartTime &&
        startTimeEditRef.current &&
        !startTimeEditRef.current.contains(event.target)
      ) {
        handleSaveStartTime();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditingStartTime, dayStartTime]);

  // 인라인 활동 카드 컴포넌트
  const InlineActivityCard = React.memo(({
    activity = null,
    index = -1,
    isEditing = false,
    isAddMode = false,
    onEdit,
    onSave,
    onCancel,
    onDelete,
    onToggleHide,
    startTime = null,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    onDragLeave,
    categories = [],
    showCategoryManagement,
    setShowCategoryManagement,
    onAddCategory,
    onDeleteCategory,
    onChangeCategoryColor,
    newCategoryName,
    setNewCategoryName,
    newCategoryColor,
    setNewCategoryColor,
    travelPlanParticipants = [], // travelPlan.participants를 prop으로 전달받음
    isBuffer = false, // 버퍼 활동 여부
  }) => {
    // 추가 모드인 경우 빈 객체로 초기화
    const initialActivity = isAddMode
      ? {
          title: "",
          location: "",
          type: "duration", // 기본값: 소요시간 타입
          duration: 0,
          budget: "",
          people: [],
          description: "",
          category: null,
          startTime: startTime,
          endTime: startTime,
        }
      : activity;

    const [localActivity, setLocalActivity] = useState(initialActivity);
    // 참여자 dropdown 상태 (편집용)
    const [isParticipantsDropdownOpen, setIsParticipantsDropdownOpen] =
      useState(false);
    // 편집 영역 참조
    const editCardRef = useRef(null);
    // 더보기 메뉴 상태
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const moreMenuRef = useRef(null);

    useEffect(() => {
      if (!isAddMode) {
        // fix 타입인 경우 fixStartTime/fixEndTime 초기화
        const activityWithFixTime = { ...activity };
        if (activity.type === 'fix') {
          activityWithFixTime.fixStartTime = activity.startTime;
          activityWithFixTime.fixEndTime = activity.endTime;
        }
        setLocalActivity(activityWithFixTime);
      } else {
        // 추가 모드에서 전체 참여자 기본 선택
        if (travelPlanParticipants && travelPlanParticipants.length > 0) {
          const allParticipantIds = travelPlanParticipants.map((p) => p.id);
          setLocalActivity((prev) => ({ ...prev, people: allParticipantIds }));
        }
      }
    }, [activity, isAddMode, travelPlanParticipants]);

    const handleFieldChange = (field, value) => {
      const updatedActivity = { ...localActivity, [field]: value };

      // 소요시간이 변경되면 종료시간 자동 계산
      if (field === "duration") {
        const durationMinutes = parseInt(value) || 0;
        updatedActivity.endTime = addMinutes(
          updatedActivity.startTime,
          durationMinutes
        );
        updatedActivity.duration = durationMinutes;
      }

      setLocalActivity(updatedActivity);
    };

    // 참여자 선택/해제 토글 (편집용)
    const handleParticipantToggleEdit = (participantId) => {
      const currentPeople = Array.isArray(localActivity.people)
        ? localActivity.people
        : [];
      const updatedPeople = currentPeople.includes(participantId)
        ? currentPeople.filter((id) => id !== participantId)
        : [...currentPeople, participantId];

      handleFieldChange("people", updatedPeople);
    };

    // 모든 참여자 선택/해제 (편집용)
    const handleSelectAllParticipantsEdit = () => {
      const allParticipantIds =
        travelPlanParticipants?.map((p) => p.id) || [];
      const currentPeople = Array.isArray(localActivity.people)
        ? localActivity.people
        : [];
      const updatedPeople =
        currentPeople.length === allParticipantIds.length
          ? []
          : allParticipantIds;

      handleFieldChange("people", updatedPeople);
    };

    const handleSave = () => {
      if (!localActivity.title.trim()) {
        alert("계획 제목은 필수입니다.");
        return;
      }

      if (isAddMode) {
        // 추가 모드에서는 활동 데이터만 전달
        onSave(localActivity);
      } else {
        // 편집 모드에서는 기존 방식 유지
        onSave(localActivity, true, activity.id);
        onCancel();
      }
    };

    // 편집 중 외부 클릭 감지
    useEffect(() => {
      if (!isEditing && !isAddMode) return;

      const handleClickOutside = (event) => {
        if (
          editCardRef.current &&
          !editCardRef.current.contains(event.target)
        ) {
          // 제목이 입력되지 않았으면 저장하지 않고 취소
          if (!localActivity.title.trim()) {
            onCancel();
            return;
          }
          handleSave();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isEditing, isAddMode, localActivity]);

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onCancel();
      } else if (e.key === "Enter") {
        e.preventDefault(); // 기본 동작 방지
        handleSave();
      }
    };

    // 더보기 메뉴 외부 클릭 감지
    useEffect(() => {
      if (!showMoreMenu) return;

      const handleClickOutside = (event) => {
        if (
          moreMenuRef.current &&
          !moreMenuRef.current.contains(event.target)
        ) {
          setShowMoreMenu(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [showMoreMenu]);

    if (isEditing || isAddMode) {
      return (
        <div
          className={`activity-item editing ${isAddMode ? "add-form" : ""}`}
          ref={editCardRef}
        >
          <div className="activity-time-editing">
            <span className="time-text">{localActivity.startTime}</span>
            <span className="time-separator">~</span>
            <span className="time-text">{localActivity.endTime}</span>
          </div>

          <div className="activity-content">
            {/* 제목 입력 */}
            <div className="input-group title-group" style={{ marginBottom: '12px' }}>
              <label className="input-label">제목</label>
              <input
                type="text"
                value={localActivity.title}
                onChange={(e) => handleFieldChange("title", e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="계획 제목을 입력하세요"
                className="title-input"
                autoFocus
              />
            </div>

            {/* 타입 선택 + 입력 필드 (1행) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              {/* 타입 선택 버튼 */}
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  type="button"
                  className={`type-btn ${(!localActivity.type || localActivity.type === 'duration') ? 'selected' : ''}`}
                  onClick={() => handleFieldChange("type", "duration")}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    background: (!localActivity.type || localActivity.type === 'duration') ? '#007bff' : '#fff',
                    color: (!localActivity.type || localActivity.type === 'duration') ? '#fff' : '#333',
                    cursor: 'pointer',
                    fontSize: '13px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  소요시간
                </button>
                <button
                  type="button"
                  className={`type-btn ${localActivity.type === 'fix' ? 'selected' : ''}`}
                  onClick={() => handleFieldChange("type", "fix")}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    background: localActivity.type === 'fix' ? '#007bff' : '#fff',
                    color: localActivity.type === 'fix' ? '#fff' : '#333',
                    cursor: 'pointer',
                    fontSize: '13px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  고정시각
                </button>
              </div>

              {/* 타입별 입력 필드 */}
              {(!localActivity.type || localActivity.type === 'duration') ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="number"
                    value={
                      localActivity.duration === 0 ||
                      localActivity.duration === "0"
                        ? ""
                        : localActivity.duration
                    }
                    onChange={(e) =>
                      handleFieldChange("duration", e.target.value)
                    }
                    onKeyDown={handleKeyDown}
                    onBlur={(e) => {
                      if (
                        e.target.value === "" ||
                        e.target.value === null ||
                        e.target.value === undefined
                      ) {
                        handleFieldChange("duration", "0");
                      }
                    }}
                    placeholder="0"
                    className="duration-input"
                    min="0"
                    style={{ width: '60px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                  <span style={{ fontSize: '13px' }}>분</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="time"
                    value={localActivity.fixStartTime || localActivity.startTime || ""}
                    onChange={(e) => handleFieldChange("fixStartTime", e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="time-input"
                    style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px' }}
                  />
                  <span style={{ fontSize: '13px' }}>~</span>
                  <input
                    type="time"
                    value={localActivity.fixEndTime || localActivity.endTime || ""}
                    onChange={(e) => handleFieldChange("fixEndTime", e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="time-input"
                    style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px' }}
                  />
                </div>
              )}
            </div>

            <div className="activity-edit-details">
              {/* 카테고리 선택 */}
              <div className="edit-row category-row">
                <div className="input-group category-group">
                  <label className="input-label">카테고리</label>
                  <div className="category-selection">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        className={`category-btn ${
                          localActivity.category === cat.id ? "selected" : ""
                        }`}
                        style={{
                          backgroundColor:
                            localActivity.category === cat.id
                              ? cat.color
                              : "transparent",
                          borderColor: cat.color,
                          color:
                            localActivity.category === cat.id ? "white" : cat.color,
                        }}
                        onClick={() => {
                          handleFieldChange(
                            "category",
                            localActivity.category === cat.id ? null : cat.id
                          );
                        }}
                      >
                        {cat.name}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="category-manage-btn"
                      onClick={() => setShowCategoryManagement(!showCategoryManagement)}
                    >
                      ⚙️
                    </button>
                  </div>
                </div>
              </div>

              {/* 카테고리 관리 모달 */}
              {showCategoryManagement && (
                <div className="category-management-panel">
                  <h4>카테고리 관리</h4>
                  <div className="category-list">
                    {categories.map((cat) => (
                      <div key={cat.id} className="category-item">
                        <span className="category-name">{cat.name}</span>
                        <input
                          type="color"
                          value={cat.color}
                          onChange={(e) =>
                            onChangeCategoryColor(cat.id, e.target.value)
                          }
                          className="category-color-picker"
                        />
                        <button
                          type="button"
                          className="category-delete-btn"
                          onClick={() => onDeleteCategory(cat.id)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="category-add-form">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="새 카테고리 이름"
                      className="category-name-input"
                    />
                    <input
                      type="color"
                      value={newCategoryColor}
                      onChange={(e) => setNewCategoryColor(e.target.value)}
                      className="category-color-picker"
                    />
                    <button
                      type="button"
                      className="category-add-btn"
                      onClick={onAddCategory}
                    >
                      추가
                    </button>
                  </div>
                </div>
              )}

              <div className="edit-row location-budget-people-row">
                <div className="input-group location-group">
                  <label className="input-label">장소</label>
                  <input
                    type="text"
                    value={localActivity.location}
                    onChange={(e) =>
                      handleFieldChange("location", e.target.value)
                    }
                    onKeyDown={handleKeyDown}
                    placeholder="📍 장소를 입력하세요"
                    className="location-input"
                  />
                </div>
                <div className="input-group budget-group">
                  <label className="input-label">예산</label>
                  <input
                    type="text"
                    value={localActivity.budget}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleFieldChange("budget", value);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="💰 예산 (원)"
                    className="budget-input"
                  />
                </div>
                <div className="input-group participants-group">
                  <div className="participants-header">
                    <label className="input-label">
                      참여자 (
                      {Array.isArray(localActivity.people)
                        ? localActivity.people.length
                        : 0}
                      명)
                    </label>
                    {travelPlanParticipants &&
                      travelPlanParticipants.length > 0 && (
                        <button
                          type="button"
                          onClick={handleSelectAllParticipantsEdit}
                          className="select-all-toggle-btn"
                        >
                          {Array.isArray(localActivity.people) &&
                          localActivity.people.length ===
                            travelPlanParticipants.length
                            ? "전체 해제"
                            : "전체 선택"}
                        </button>
                      )}
                  </div>

                  {/* 참여자가 등록되어 있는 경우 */}
                  {travelPlanParticipants &&
                  travelPlanParticipants.length > 0 ? (
                    <div className="participants-toggle-container">
                      {travelPlanParticipants.map((participant) => (
                        <button
                          key={participant.id}
                          type="button"
                          className={`participant-toggle-label ${
                            Array.isArray(localActivity.people) &&
                            localActivity.people.includes(participant.id)
                              ? "selected"
                              : ""
                          }`}
                          onClick={() =>
                            handleParticipantToggleEdit(participant.id)
                          }
                        >
                          {participant.name}
                        </button>
                      ))}
                    </div>
                  ) : (
                    /* 참여자가 등록되지 않은 경우 비활성화된 입력 */
                    <input
                      type="text"
                      value="등록된 참여자가 없습니다."
                      className="people-input disabled"
                      disabled
                      readOnly
                      style={{ pointerEvents: "none" }}
                    />
                  )}
                </div>
              </div>

              <div className="input-group">
                <label className="input-label">메모</label>
                <RichTextEditor
                  value={localActivity.description}
                  onChange={(content) =>
                    handleFieldChange("description", content)
                  }
                  placeholder="추가 메모나 상세 설명을 입력하세요 (이미지는 Ctrl+V로 붙여넣기)"
                  height="120px"
                  localMode={!isAuthenticated}
                />
              </div>

              {/* 확인/취소 버튼 - 맨 아래로 이동 */}
              <div className="activity-actions-bottom">
                <button className="save-btn" onClick={handleSave}>
                  ✓ 확인
                </button>
                <button className="cancel-btn" onClick={onCancel}>
                  ✕ 취소
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const isDraggable = !isEditing && !isAddMode && !isBuffer;

    // 카테고리 색상 적용
    const getCategoryColor = () => {
      if (!activity.category) return {};
      const category = categories.find((c) => c.id === activity.category);
      if (!category) {
        console.log('⚠️ [활동 카드] 카테고리를 찾을 수 없음:', {
          activityId: activity.id,
          categoryId: activity.category,
          availableCategories: categories.map(c => ({ id: c.id, name: c.name }))
        });
        return {};
      }

      // 카테고리 색상의 30% 불투명도
      const hexToRgba = (hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      return {
        backgroundColor: hexToRgba(category.color, 0.3),
        borderLeft: `4px solid ${category.color}`,
      };
    };

    return (
      <div
        className={`activity-item ${activity.hidden ? 'hidden' : ''} ${showMoreMenu ? 'menu-open' : ''} ${isBuffer ? 'buffer-activity' : ''}`}
        style={isBuffer ? { backgroundColor: '#f5f5f5', borderLeft: '4px solid #bdbdbd' } : getCategoryColor()}
        draggable={isDraggable}
        onDragStart={(e) => {
          if (isDraggable && onDragStart) {
            onDragStart(e, index);
          }
        }}
        onDragOver={(e) => onDragOver && onDragOver(e, index)}
        onDrop={(e) => onDrop && onDrop(e, index)}
        onDragEnd={onDragEnd}
        onDragLeave={onDragLeave}
        onDoubleClick={(e) => {
          if (isBuffer) return; // 버퍼는 더블클릭 무시
          if (moreMenuRef.current && moreMenuRef.current.contains(e.target)) {
            return;
          }
          !isEditing && onEdit(index);
        }}
      >
        {/* 드래그 핸들 */}
        {!isEditing && !isAddMode && !isBuffer && (
          <div className="drag-handle" title="드래그하여 순서 변경">
            <span className="drag-icon">⋮⋮</span>
          </div>
        )}

        {/* 첫 번째 줄: 시간 + 소요시간 + 제목 (왼쪽) | 더보기 버튼 (오른쪽) */}
        <div className="activity-row-1">
          <div className="activity-left">
            {activity.type === 'fix' ? (
              // fix 타입: 시작시각 ~ 종료시각 형식
              <>
                <div className="activity-time-inline">{activity.startTime}</div>
                <div className="activity-time-inline">~{activity.endTime}</div>
              </>
            ) : (
              // duration 타입 (버퍼 포함): 종료시각 + 소요시간
              <>
                <div className="activity-time-inline">{isBuffer ? activity.startTime : activity.endTime}</div>
                <div className="activity-duration-inline">{activity.duration}m</div>
              </>
            )}
            <h3 className="activity-title-inline" style={isBuffer ? { color: '#757575' } : {}}>{activity.title}</h3>
          </div>
          {!isBuffer && (
            <div className="activity-right" ref={moreMenuRef}>
              <button
                className="more-btn-activity"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMoreMenu(!showMoreMenu);
                }}
              >
                ⋮
              </button>
              {showMoreMenu && (
                <div className="more-menu-dropdown">
                  <button
                    className="menu-item edit-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMoreMenu(false);
                      onEdit(index);
                    }}
                  >
                    ✏️ 편집
                  </button>
                  <button
                    className="menu-item hide-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMoreMenu(false);
                      onToggleHide(activity.id);
                    }}
                  >
                    {activity.hidden ? "👁️ 숨기기 취소" : "🙈 숨기기"}
                  </button>
                  <button
                    className="menu-item delete-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMoreMenu(false);
                      onDelete(activity.id);
                    }}
                  >
                    🗑️ 삭제
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 두 번째 줄: 장소, 예산, 참여자 (오른쪽 정렬) - 버퍼는 표시 안 함 */}
        {!isBuffer && (
          <>
            <div className="activity-row-2">
              <div className="activity-details-right">
                {activity.location && (
                  <div className="detail-item-inline">
                    <span className="detail-icon">📍</span>
                    <span>{activity.location}</span>
                  </div>
                )}

                {activity.budget > 0 && (
                  <div className="detail-item-inline">
                    <span className="detail-icon">💰</span>
                    <span>{activity.budget.toLocaleString()}원</span>
                  </div>
                )}

                {activity.people &&
                  activity.people.length > 0 &&
                  travelPlan?.participants &&
                  travelPlan.participants.length > 0 && (
                    <div className="detail-item-inline">
                      <span className="detail-icon">👥</span>
                      <span>
                        {activity.people
                          .map((participantId) => {
                            const participant = travelPlan.participants.find(
                              (p) => p.id === participantId
                            );
                            return participant ? participant.name : null;
                          })
                          .filter(name => name !== null)
                          .join(", ")}{" "}
                        ({activity.people.length}명)
                      </span>
                    </div>
                  )}
              </div>
            </div>

            {activity.description && (
              <div className="activity-description">
                <div dangerouslySetInnerHTML={{ __html: convertImageUrlsForProxy(activity.description) }} />
              </div>
            )}
          </>
        )}
      </div>
    );
  }, (prevProps, nextProps) => {
    // 최적화: 관련 props가 변경되지 않으면 리렌더링 안 함
    return (
      prevProps.activity?.id === nextProps.activity?.id &&
      prevProps.index === nextProps.index &&
      prevProps.isEditing === nextProps.isEditing &&
      prevProps.isAddMode === nextProps.isAddMode
    );
  });

  // 활동 삭제
  const handleDeleteActivity = React.useCallback(async (activityId) => {
    if (!travelPlan.days || !travelPlan.days[selectedDay]) {
      console.error('❌ [handleDeleteActivity] days 배열 또는 선택된 day가 없음');
      return;
    }

    if (window.confirm("이 활동을 삭제하시겠습니까?")) {
      // 서버에서 삭제
      const isServerPlan = typeof travelPlan.id === 'number' || (typeof travelPlan.id === 'string' && !travelPlan.id.startsWith('local-'));
      const isServerActivityId = typeof activityId === 'number' || (typeof activityId === 'string' && !activityId.startsWith('local-'));

      if (isServerPlan && isServerActivityId && isAuthenticated) {
        try {
          const dayId = selectedDay + 1;
          await travelApiService.deleteActivity(travelPlan.id, dayId, activityId);
          console.log('✅ [활동 삭제] 서버 삭제 성공');
        } catch (error) {
          console.error('❌ [활동 삭제] 서버 삭제 실패:', error);
          // 에러가 나도 로컬에서는 삭제 진행
        }
      }

      // 로컬에서 삭제
      const updatedPlan = { ...travelPlan };
      updatedPlan.days[selectedDay].activities = (updatedPlan.days[selectedDay].activities || []).filter((a) => a.id !== activityId);
      saveTravelPlan(updatedPlan);
    }
  }, [travelPlan, selectedDay, isAuthenticated]);

  // 활동 숨기기/숨기기 취소
  const handleToggleHideActivity = React.useCallback((activityId) => {
    if (!travelPlan.days || !travelPlan.days[selectedDay]) {
      console.error('❌ [handleToggleHideActivity] days 배열 또는 선택된 day가 없음');
      return;
    }

    const updatedPlan = { ...travelPlan };
    const dayActivities = [...(updatedPlan.days[selectedDay].activities || [])];

    const activityIndex = dayActivities.findIndex((a) => a.id === activityId);
    if (activityIndex === -1) return;

    const activity = dayActivities[activityIndex];

    if (activity.hidden) {
      // 숨기기 취소: 원래 소요시간 복구
      dayActivities[activityIndex] = {
        ...activity,
        hidden: false,
        duration: activity.originalDuration || 0,
      };
    } else {
      // 숨기기: 현재 소요시간 저장하고 0으로 설정
      dayActivities[activityIndex] = {
        ...activity,
        hidden: true,
        originalDuration: activity.duration,
        duration: 0,
      };
    }

    // 모든 활동의 시간을 순차적으로 재계산
    const recalculatedActivities = recalculateActivityTimes(
      dayActivities,
      dayStartTime
    );

    updatedPlan.days[selectedDay].activities = recalculatedActivities;
    saveTravelPlan(updatedPlan);
  }, [travelPlan, selectedDay, dayStartTime, recalculateActivityTimes]);

  // 드래그 시작
  const handleDragStart = React.useCallback((e, index) => {
    draggedIndexRef.current = index;
    insertPositionRef.current = index;
    draggedElementRef.current = e.currentTarget;

    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());

    // DOM 직접 조작으로 드래그 효과 추가
    if (draggedElementRef.current) {
      draggedElementRef.current.classList.add('dragging');
    }

    // 드롭 인디케이터 생성
    const indicator = document.createElement('div');
    indicator.className = 'drop-indicator';
    dropIndicatorRef.current = indicator;
  }, []);

  // 드래그 중 다른 카드 위로 이동
  const handleDragOver = React.useCallback((e, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";

    const draggedIndex = draggedIndexRef.current;
    if (draggedIndex === null) return;

    const targetElement = e.currentTarget;
    if (!targetElement) return;

    const rect = targetElement.getBoundingClientRect();
    const mouseY = e.clientY;
    const cardMiddle = rect.top + rect.height / 2;

    // 마우스가 카드의 위쪽 절반에 있으면 그 앞에, 아래쪽이면 그 뒤에 삽입
    let newInsertPosition;
    if (mouseY < cardMiddle) {
      newInsertPosition = index;
    } else {
      newInsertPosition = index + 1;
    }

    // 삽입 위치가 변경되지 않았으면 리턴
    if (insertPositionRef.current === newInsertPosition) return;

    insertPositionRef.current = newInsertPosition;

    // 모든 카드의 shift-down 클래스 제거
    const allCards = Array.from(document.querySelectorAll('.activities-timeline > .activity-item:not(.dragging)'));

    allCards.forEach(card => {
      card.classList.remove('shift-down');
    });

    // 삽입 위치 계산 (draggedIndex 제거 후)
    let adjustedInsertPos = newInsertPosition;
    if (draggedIndex < newInsertPosition) {
      adjustedInsertPos = newInsertPosition - 1;
    }

    // 삽입 위치 이후의 카드들을 아래로 이동
    const allCardsWithDragging = document.querySelectorAll('.activities-timeline > .activity-item');
    allCardsWithDragging.forEach((card, idx) => {
      if (idx === draggedIndex) return; // 드래그 중인 카드는 건너뜀

      const adjustedIdx = idx > draggedIndex ? idx - 1 : idx;

      if (adjustedIdx >= adjustedInsertPos) {
        card.classList.add('shift-down');
      }
    });

    // 드롭 인디케이터 표시
    const timeline = document.querySelector('.activities-timeline');
    const indicator = dropIndicatorRef.current;

    if (indicator && timeline) {
      // 기존 인디케이터 제거
      if (indicator.parentNode) {
        indicator.parentNode.removeChild(indicator);
      }

      // 삽입 위치에 인디케이터 추가
      const allItems = Array.from(timeline.children);
      if (newInsertPosition >= allItems.length) {
        timeline.appendChild(indicator);
      } else {
        timeline.insertBefore(indicator, allItems[newInsertPosition]);
      }
    }
  }, []);

  // 드래그가 카드를 떠날 때 (이제 필요없음)
  const handleDragLeave = React.useCallback((e) => {
    // 카드 사이 삽입 방식에서는 불필요
  }, []);

  // 드롭
  const handleDrop = React.useCallback((e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const draggedIndex = draggedIndexRef.current;
    const insertPosition = insertPositionRef.current;

    // 모든 시각적 효과 제거
    document.querySelectorAll('.activity-item.dragging').forEach(el => {
      el.classList.remove('dragging');
    });
    document.querySelectorAll('.activity-item.shift-down').forEach(el => {
      el.classList.remove('shift-down');
    });

    // 드롭 인디케이터 제거
    if (dropIndicatorRef.current && dropIndicatorRef.current.parentNode) {
      dropIndicatorRef.current.parentNode.removeChild(dropIndicatorRef.current);
    }

    if (draggedIndex === null || insertPosition === null) {
      draggedIndexRef.current = null;
      draggedElementRef.current = null;
      insertPositionRef.current = null;
      dropIndicatorRef.current = null;
      return;
    }

    // 삽입 위치 조정 (draggedIndex 제거 후의 위치)
    let finalInsertPosition = insertPosition;
    if (draggedIndex < insertPosition) {
      finalInsertPosition = insertPosition - 1;
    }

    // 같은 위치면 취소
    if (draggedIndex === finalInsertPosition) {
      draggedIndexRef.current = null;
      draggedElementRef.current = null;
      insertPositionRef.current = null;
      dropIndicatorRef.current = null;
      return;
    }

    // 배열 재정렬
    if (!travelPlan.days || !travelPlan.days[selectedDay]) {
      console.error('❌ [handleDrop] days 배열 또는 선택된 day가 없음');
      draggedIndexRef.current = null;
      draggedElementRef.current = null;
      insertPositionRef.current = null;
      dropIndicatorRef.current = null;
      return;
    }

    const updatedPlan = { ...travelPlan };
    const activities = [...(updatedPlan.days[selectedDay].activities || [])];
    const [draggedActivity] = activities.splice(draggedIndex, 1);
    activities.splice(finalInsertPosition, 0, draggedActivity);

    // 시간 순차적으로 재계산
    const recalculatedActivities = recalculateActivityTimes(
      activities,
      dayStartTime
    );

    updatedPlan.days[selectedDay].activities = recalculatedActivities;
    saveTravelPlan(updatedPlan);

    // 서버에 순서 변경 전송
    const isServerPlan = typeof travelPlan.id === 'number' || (typeof travelPlan.id === 'string' && !travelPlan.id.startsWith('local-'));
    if (isServerPlan && isAuthenticated) {
      // 모두 서버 ID인 활동들만 순서 변경 API 호출
      const allServerIds = recalculatedActivities.every(
        (a) => typeof a.id === 'number' || (typeof a.id === 'string' && !a.id.startsWith('local-'))
      );

      if (allServerIds) {
        const dayId = selectedDay + 1;
        const activityOrders = recalculatedActivities.map((activity, index) => ({
          id: activity.id,
          activity_order: index + 1
        }));

        travelApiService.reorderActivities(travelPlan.id, dayId, activityOrders)
          .then(() => {
            console.log('✅ [활동 순서 변경] 서버 업데이트 성공');
          })
          .catch((error) => {
            console.error('❌ [활동 순서 변경] 서버 업데이트 실패:', error);
          });
      }
    }

    // 상태 초기화
    draggedIndexRef.current = null;
    draggedElementRef.current = null;
    insertPositionRef.current = null;
    dropIndicatorRef.current = null;
  }, [travelPlan, selectedDay, dayStartTime, isAuthenticated]);

  // 드래그 종료
  const handleDragEnd = React.useCallback(() => {
    // 모든 시각적 효과 제거
    document.querySelectorAll('.activity-item.dragging').forEach(el => {
      el.classList.remove('dragging');
    });
    document.querySelectorAll('.activity-item.shift-down').forEach(el => {
      el.classList.remove('shift-down');
    });

    // 드롭 인디케이터 제거
    if (dropIndicatorRef.current && dropIndicatorRef.current.parentNode) {
      dropIndicatorRef.current.parentNode.removeChild(dropIndicatorRef.current);
    }

    draggedIndexRef.current = null;
    draggedElementRef.current = null;
    insertPositionRef.current = null;
    dropIndicatorRef.current = null;
  }, []);

  // 드래그 종료 시 상태 초기화 (전역 이벤트)
  useEffect(() => {
    const cleanup = () => {
      // 모든 시각적 효과 제거
      document.querySelectorAll('.activity-item.dragging').forEach(el => {
        el.classList.remove('dragging');
      });
      document.querySelectorAll('.activity-item.shift-down').forEach(el => {
        el.classList.remove('shift-down');
      });

      // 드롭 인디케이터 제거
      if (dropIndicatorRef.current && dropIndicatorRef.current.parentNode) {
        dropIndicatorRef.current.parentNode.removeChild(dropIndicatorRef.current);
      }

      draggedIndexRef.current = null;
      draggedElementRef.current = null;
      insertPositionRef.current = null;
      dropIndicatorRef.current = null;
    };

    document.addEventListener('dragend', cleanup);
    document.addEventListener('mouseup', cleanup);

    return () => {
      document.removeEventListener('dragend', cleanup);
      document.removeEventListener('mouseup', cleanup);
    };
  }, []);

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", {
      month: "long",
      day: "numeric",
      weekday: "short",
    });
  };

  // 예산 계산
  const calculateDayBudget = (activities) => {
    if (!activities || !Array.isArray(activities)) return 0;
    return activities.reduce((total, activity) => {
      const budget = parseInt(activity.budget) || 0;
      return total + budget;
    }, 0);
  };

  // 총 예산 계산 (활동 + 교통 + 숙박)
  const calculateTotalBudget = () => {
    if (!travelPlan) return 0;

    // 1. 활동 예산
    let activitiesBudget = 0;
    if (travelPlan.days) {
      activitiesBudget = travelPlan.days.reduce(
        (total, day) => total + calculateDayBudget(day?.activities),
        0
      );
    }

    // 2. 교통 예약 예산
    const transportationsBudget = transportations.reduce((total, transport) => {
      const budget = parseInt(transport.budget) || 0;
      return total + budget;
    }, 0);

    // 3. 숙박 예약 예산
    const accommodationsBudget = accommodations.reduce((total, accommodation) => {
      const budget = parseInt(accommodation.budget) || 0;
      return total + budget;
    }, 0);

    return activitiesBudget + transportationsBudget + accommodationsBudget;
  };

  // 여행 정보 편집 시작
  const handleStartEditTravelInfo = () => {
    setEditedTravelInfo({
      title: travelPlan.title,
      destination: travelPlan.destination,
      startDate: travelPlan.startDate,
      endDate: travelPlan.endDate,
      budget: travelPlan.budget || "",
      description: travelPlan.description || "",
    });
    setIsEditingTravelInfo(true);
  };

  // 여행 정보 저장
  const handleSaveTravelInfo = () => {
    // 날짜 유효성 검사
    const startDate = new Date(editedTravelInfo.startDate);
    const endDate = new Date(editedTravelInfo.endDate);

    if (endDate < startDate) {
      alert("종료 날짜는 시작 날짜보다 늦어야 합니다.");
      return;
    }

    // 일수 계산
    const duration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

    // days 배열 재구성
    const newDays = [];
    for (let i = 0; i < duration; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const dateString = currentDate.toISOString().split("T")[0];

      // 기존 날짜 데이터가 있으면 유지, 없으면 새로 생성
      const existingDay = travelPlan.days.find((day) => day.date === dateString);
      newDays.push(
        existingDay || {
          date: dateString,
          activities: [],
          startTime: "09:00",
        }
      );
    }

    const updatedPlan = {
      ...travelPlan,
      title: editedTravelInfo.title,
      destination: editedTravelInfo.destination,
      startDate: editedTravelInfo.startDate,
      endDate: editedTravelInfo.endDate,
      duration: duration,
      budget: parseInt(editedTravelInfo.budget) || 0,
      description: editedTravelInfo.description,
      days: newDays,
    };

    saveTravelPlan(updatedPlan);
    setIsEditingTravelInfo(false);

    // 선택된 날짜가 범위를 벗어나면 첫 번째 날로 이동
    if (selectedDay >= duration) {
      setSelectedDay(0);
    }
  };

  // 여행 정보 편집 취소
  const handleCancelEditTravelInfo = () => {
    setIsEditingTravelInfo(false);
    setEditedTravelInfo({
      title: "",
      destination: "",
      startDate: "",
      endDate: "",
      budget: "",
      description: "",
    });
  };

  // 페이지 타입별 제목 가져오기
  const getPageTitle = (pageType) => {
    switch(pageType) {
      case 'info':
        return '여행 정보';
      case 'booking':
        return '예약 정보';
      case 'checklist':
        return '체크 리스트';
      case 'shared':
        return '공유 관리';
      default:
        return '여행 정보';
    }
  };

  // 페이지 아이콘 가져오기
  const getPageIcon = (pageType) => {
    switch (pageType) {
      case 'info':
        return 'ℹ️';
      case 'booking':
        return '🎫';
      case 'checklist':
        return '✅';
      case 'shared':
        return '🔗';
      default:
        return 'ℹ️';
    }
  };

  // 페이지 전환 (왼쪽 화살표)
  const handlePreviousPage = () => {
    const pages = ['info', 'booking', 'checklist', 'shared'];
    const currentIndex = pages.indexOf(currentPageType);
    const prevIndex = currentIndex === 0 ? pages.length - 1 : currentIndex - 1;
    setCurrentPageType(pages[prevIndex]);
    setShowTravelInfoModal(true);
  };

  // 페이지 전환 (오른쪽 화살표)
  const handleNextPage = () => {
    const pages = ['info', 'booking', 'checklist', 'shared'];
    const currentIndex = pages.indexOf(currentPageType);
    const nextIndex = currentIndex === pages.length - 1 ? 0 : currentIndex + 1;
    setCurrentPageType(pages[nextIndex]);
    setShowTravelInfoModal(true);
  };

  // 이전/다음 페이지 타입 계산
  const getPreviousPageType = () => {
    const pages = ['info', 'booking', 'checklist', 'shared'];
    const currentIndex = pages.indexOf(currentPageType);
    const prevIndex = currentIndex === 0 ? pages.length - 1 : currentIndex - 1;
    return pages[prevIndex];
  };

  const getNextPageType = () => {
    const pages = ['info', 'booking', 'checklist', 'shared'];
    const currentIndex = pages.indexOf(currentPageType);
    const nextIndex = currentIndex === pages.length - 1 ? 0 : currentIndex + 1;
    return pages[nextIndex];
  };

  // 더보기 메뉴에서 페이지 선택
  const handleSelectPage = (pageType) => {
    setCurrentPageType(pageType);
    setShowTravelInfoModal(true);
    setShowMoreMenu(false);
  };

  // 교통 정보 추가
  const handleAddTransport = async () => {
    if (!newTransport.type.trim()) {
      alert('교통 수단은 필수입니다.');
      return;
    }

    const localTransportId = `local-${Date.now()}`;
    const transport = {
      id: localTransportId,
      type: newTransport.type.trim(),
      reservationNumber: newTransport.reservationNumber.trim(),
      budget: parseInt(newTransport.budget) || 0,
      people: newTransport.people || [],
      memo: newTransport.memo
    };

    // 1. 먼저 로컬 상태 업데이트
    const updatedTransportations = [...transportations, transport];
    setTransportations(updatedTransportations);

    // 2. localStorage에 저장
    const updatedPlan = {
      ...travelPlan,
      transportations: updatedTransportations
    };
    saveTravelPlan(updatedPlan);

    // 3. 서버 저장 (로그인 상태 & 서버 ID인 경우)
    const isServerPlan = typeof travelPlan.id === 'number' || (typeof travelPlan.id === 'string' && !travelPlan.id.startsWith('local-'));
    if (isAuthenticated && isServerPlan) {
      try {
        console.log('🔄 [교통 정보 추가] 서버 저장 중...');
        const apiData = {
          type: newTransport.type.trim(),
          reservation_number: newTransport.reservationNumber.trim() || undefined,
          budget: parseInt(newTransport.budget) || 0,
          participants: newTransport.people || [],
          memo: newTransport.memo || undefined
        };

        const response = await travelApiService.createTransportation(travelPlan.id, apiData);

        if (response.success && response.data && response.data.id) {
          const serverId = response.data.id;
          console.log('✅ [교통 정보 추가] 서버 저장 성공, ID:', serverId);

          // 4. 서버 ID로 교체
          const transportWithRealId = {
            ...transport,
            id: serverId
          };
          const syncedTransportations = updatedTransportations.map(t =>
            t.id === localTransportId ? transportWithRealId : t
          );
          setTransportations(syncedTransportations);

          // localStorage도 업데이트
          const syncedPlan = {
            ...updatedPlan,
            transportations: syncedTransportations
          };
          saveTravelPlan(syncedPlan);
        }
      } catch (error) {
        console.error('❌ [교통 정보 추가] 서버 저장 실패:', error);
      }
    }

    // 폼 초기화
    setNewTransport({ type: '', reservationNumber: '', budget: '', people: [], memo: '' });
    setShowAddTransport(false);
  };

  // 교통 정보 수정 시작
  const handleEditTransport = (transport) => {
    setEditingTransportId(transport.id);
    setNewTransport({
      type: transport.type || '',
      reservationNumber: transport.reservation_number || transport.reservationNumber || '',
      budget: transport.budget || '',
      people: transport.participants ? transport.participants.map(p => p.id) : (transport.people || []),
      memo: transport.memo || ''
    });
  };

  // 교통 정보 수정 저장
  const handleUpdateTransport = async () => {
    if (!newTransport.type.trim()) {
      alert('교통 수단은 필수입니다.');
      return;
    }

    const transportId = editingTransportId;
    const isLocalId = typeof transportId === 'string' && transportId.startsWith('local-');
    const isServerPlan = typeof travelPlan.id === 'number' || (typeof travelPlan.id === 'string' && !travelPlan.id.startsWith('local-'));

    // 서버에 저장하는 경우, 서버 응답을 기다렸다가 상태를 업데이트
    if (!isLocalId && isAuthenticated && isServerPlan) {
      try {
        console.log('🔄 [교통 정보 수정] 서버 저장 중...');
        const apiData = {
          type: newTransport.type.trim(),
          reservation_number: newTransport.reservationNumber.trim() || undefined,
          budget: parseInt(newTransport.budget) || 0,
          participants: newTransport.people || [],
          memo: newTransport.memo || undefined
        };

        const response = await travelApiService.updateTransportation(travelPlan.id, transportId, apiData);

        if (response.success && response.data) {
          console.log('✅ [교통 정보 수정] 서버 저장 성공:', response.data);

          // 서버 응답 데이터로 업데이트 (participants 객체 배열 사용)
          const updatedTransportations = transportations.map(t =>
            t.id === transportId ? response.data : t
          );
          setTransportations(updatedTransportations);

          // localStorage도 업데이트
          const updatedPlan = {
            ...travelPlan,
            transportations: updatedTransportations
          };
          saveTravelPlan(updatedPlan);
        }
      } catch (error) {
        console.error('❌ [교통 정보 수정] 서버 저장 실패:', error);
        alert('서버 저장에 실패했습니다. 다시 시도해주세요.');
        return; // 실패하면 폼을 닫지 않음
      }
    } else {
      // 로컬 전용 업데이트 (서버 없이)
      const originalTransport = transportations.find(t => t.id === transportId);
      const updatedTransport = {
        ...originalTransport,
        type: newTransport.type.trim(),
        reservationNumber: newTransport.reservationNumber.trim(),
        reservation_number: newTransport.reservationNumber.trim(),
        budget: parseInt(newTransport.budget) || 0,
        people: newTransport.people || [],
        memo: newTransport.memo
      };

      const updatedTransportations = transportations.map(t =>
        t.id === transportId ? updatedTransport : t
      );
      setTransportations(updatedTransportations);

      // localStorage에 저장
      const updatedPlan = {
        ...travelPlan,
        transportations: updatedTransportations
      };
      saveTravelPlan(updatedPlan);
    }

    // 편집 모드 종료
    setEditingTransportId(null);
    setNewTransport({ type: '', reservationNumber: '', budget: '', people: [], memo: '' });
  };

  // 교통 정보 수정 취소
  const handleCancelEditTransport = () => {
    setEditingTransportId(null);
    setNewTransport({ type: '', reservationNumber: '', budget: '', people: [], memo: '' });
  };

  // 교통 정보 삭제
  const handleDeleteTransport = async (transportId) => {
    if (!window.confirm('이 교통 정보를 삭제하시겠습니까?')) {
      return;
    }

    const isLocalId = typeof transportId === 'string' && transportId.startsWith('local-');

    // 1. 로컬 상태에서 삭제
    const updatedTransportations = transportations.filter(t => t.id !== transportId);
    setTransportations(updatedTransportations);

    // 2. localStorage에 저장
    const updatedPlan = {
      ...travelPlan,
      transportations: updatedTransportations
    };
    saveTravelPlan(updatedPlan);

    // 3. 서버에서도 삭제 (서버 ID이고 로그인 상태인 경우)
    const isServerPlan = typeof travelPlan.id === 'number' || (typeof travelPlan.id === 'string' && !travelPlan.id.startsWith('local-'));
    if (!isLocalId && isAuthenticated && isServerPlan) {
      try {
        console.log('🗑️ [교통 정보 삭제] 서버 삭제 중... ID:', transportId);
        const response = await travelApiService.deleteTransportation(travelPlan.id, transportId);

        if (response.success) {
          console.log('✅ [교통 정보 삭제] 서버 삭제 완료');
        }
      } catch (error) {
        console.error('❌ [교통 정보 삭제] 서버 삭제 실패:', error);
        alert(`로컬에서는 삭제되었지만 서버 삭제 실패: ${error.message}\n다시 시도해주세요.`);
      }
    } else {
      console.log('⚠️ [교통 정보 삭제] 로컬에만 저장된 정보 삭제 (서버 요청 없음)');
    }
  };

  // 숙박 정보 추가
  const handleAddAccommodation = async () => {
    if (!newAccommodation.name.trim()) {
      alert('숙소명은 필수입니다.');
      return;
    }

    const localAccommodationId = `local-${Date.now()}`;
    const accommodation = {
      id: localAccommodationId,
      name: newAccommodation.name.trim(),
      address: newAccommodation.address.trim(),
      reservationNumber: newAccommodation.reservationNumber.trim(),
      budget: parseInt(newAccommodation.budget) || 0,
      people: newAccommodation.people || [],
      memo: newAccommodation.memo
    };

    // 1. 먼저 로컬 상태 업데이트
    const updatedAccommodations = [...accommodations, accommodation];
    setAccommodations(updatedAccommodations);

    // 2. localStorage에 저장
    const updatedPlan = {
      ...travelPlan,
      accommodations: updatedAccommodations
    };
    saveTravelPlan(updatedPlan);

    // 3. 서버 저장 (로그인 상태 & 서버 ID인 경우)
    const isServerPlan = typeof travelPlan.id === 'number' || (typeof travelPlan.id === 'string' && !travelPlan.id.startsWith('local-'));
    if (isAuthenticated && isServerPlan) {
      try {
        console.log('🔄 [숙박 정보 추가] 서버 저장 중...');
        const apiData = {
          name: newAccommodation.name.trim(),
          address: newAccommodation.address.trim() || undefined,
          reservation_number: newAccommodation.reservationNumber.trim() || undefined,
          budget: parseInt(newAccommodation.budget) || 0,
          participants: newAccommodation.people || [],
          memo: newAccommodation.memo || undefined
        };

        const response = await travelApiService.createAccommodation(travelPlan.id, apiData);

        if (response.success && response.data && response.data.id) {
          const serverId = response.data.id;
          console.log('✅ [숙박 정보 추가] 서버 저장 성공, ID:', serverId);

          // 4. 서버 ID로 교체
          const accommodationWithRealId = {
            ...accommodation,
            id: serverId
          };
          const syncedAccommodations = updatedAccommodations.map(a =>
            a.id === localAccommodationId ? accommodationWithRealId : a
          );
          setAccommodations(syncedAccommodations);

          // localStorage도 업데이트
          const syncedPlan = {
            ...updatedPlan,
            accommodations: syncedAccommodations
          };
          saveTravelPlan(syncedPlan);
        }
      } catch (error) {
        console.error('❌ [숙박 정보 추가] 서버 저장 실패:', error);
      }
    }

    // 폼 초기화
    setNewAccommodation({ name: '', address: '', reservationNumber: '', budget: '', people: [], memo: '' });
    setShowAddAccommodation(false);
  };

  // 숙박 정보 수정 시작
  const handleEditAccommodation = (accommodation) => {
    const peopleIds = accommodation.participants ? accommodation.participants.map(p => p.id) : (accommodation.people || []);

    setEditingAccommodationId(accommodation.id);
    setNewAccommodation({
      name: accommodation.name || '',
      address: accommodation.address || '',
      reservationNumber: accommodation.reservation_number || accommodation.reservationNumber || '',
      budget: accommodation.budget || '',
      people: peopleIds,
      memo: accommodation.memo || ''
    });
  };

  // 숙박 정보 수정 저장
  const handleUpdateAccommodation = async () => {
    if (!newAccommodation.name.trim()) {
      alert('숙소명은 필수입니다.');
      return;
    }

    const accommodationId = editingAccommodationId;
    const isLocalId = typeof accommodationId === 'string' && accommodationId.startsWith('local-');
    const isServerPlan = typeof travelPlan.id === 'number' || (typeof travelPlan.id === 'string' && !travelPlan.id.startsWith('local-'));

    // 서버에 저장하는 경우, 서버 응답을 기다렸다가 상태를 업데이트
    if (!isLocalId && isAuthenticated && isServerPlan) {
      try {
        console.log('🔄 [숙박 정보 수정] 서버 저장 중...');
        const apiData = {
          name: newAccommodation.name.trim(),
          address: newAccommodation.address.trim() || undefined,
          reservation_number: newAccommodation.reservationNumber.trim() || undefined,
          budget: parseInt(newAccommodation.budget) || 0,
          participants: newAccommodation.people || [],
          memo: newAccommodation.memo || undefined
        };

        const response = await travelApiService.updateAccommodation(travelPlan.id, accommodationId, apiData);

        if (response.success && response.data) {
          console.log('✅ [숙박 정보 수정] 서버 저장 성공:', response.data);

          // 서버 응답 데이터로 업데이트 (participants 객체 배열 사용)
          const updatedAccommodations = accommodations.map(a =>
            a.id === accommodationId ? response.data : a
          );
          setAccommodations(updatedAccommodations);

          // localStorage도 업데이트
          const updatedPlan = {
            ...travelPlan,
            accommodations: updatedAccommodations
          };
          saveTravelPlan(updatedPlan);
        }
      } catch (error) {
        console.error('❌ [숙박 정보 수정] 서버 저장 실패:', error);
        alert('서버 저장에 실패했습니다. 다시 시도해주세요.');
        return; // 실패하면 폼을 닫지 않음
      }
    } else {
      // 로컬 전용 업데이트 (서버 없이)
      const originalAccommodation = accommodations.find(a => a.id === accommodationId);
      const updatedAccommodation = {
        ...originalAccommodation,
        name: newAccommodation.name.trim(),
        address: newAccommodation.address.trim(),
        reservationNumber: newAccommodation.reservationNumber.trim(),
        reservation_number: newAccommodation.reservationNumber.trim(),
        budget: parseInt(newAccommodation.budget) || 0,
        people: newAccommodation.people || [],
        memo: newAccommodation.memo
      };

      const updatedAccommodations = accommodations.map(a =>
        a.id === accommodationId ? updatedAccommodation : a
      );
      setAccommodations(updatedAccommodations);

      // localStorage에 저장
      const updatedPlan = {
        ...travelPlan,
        accommodations: updatedAccommodations
      };
      saveTravelPlan(updatedPlan);
    }

    // 편집 모드 종료
    setEditingAccommodationId(null);
    setNewAccommodation({ name: '', address: '', reservationNumber: '', budget: '', people: [], memo: '' });
  };

  // 숙박 정보 수정 취소
  const handleCancelEditAccommodation = () => {
    setEditingAccommodationId(null);
    setNewAccommodation({ name: '', address: '', reservationNumber: '', budget: '', people: [], memo: '' });
  };

  // 숙박 정보 삭제
  const handleDeleteAccommodation = async (accommodationId) => {
    if (!window.confirm('이 숙박 정보를 삭제하시겠습니까?')) {
      return;
    }

    const isLocalId = typeof accommodationId === 'string' && accommodationId.startsWith('local-');

    // 1. 로컬 상태에서 삭제
    const updatedAccommodations = accommodations.filter(a => a.id !== accommodationId);
    setAccommodations(updatedAccommodations);

    // 2. localStorage에 저장
    const updatedPlan = {
      ...travelPlan,
      accommodations: updatedAccommodations
    };
    saveTravelPlan(updatedPlan);

    // 3. 서버에서도 삭제 (서버 ID이고 로그인 상태인 경우)
    const isServerPlan = typeof travelPlan.id === 'number' || (typeof travelPlan.id === 'string' && !travelPlan.id.startsWith('local-'));
    if (!isLocalId && isAuthenticated && isServerPlan) {
      try {
        console.log('🗑️ [숙박 정보 삭제] 서버 삭제 중... ID:', accommodationId);
        const response = await travelApiService.deleteAccommodation(travelPlan.id, accommodationId);

        if (response.success) {
          console.log('✅ [숙박 정보 삭제] 서버 삭제 완료');
        }
      } catch (error) {
        console.error('❌ [숙박 정보 삭제] 서버 삭제 실패:', error);
        alert(`로컬에서는 삭제되었지만 서버 삭제 실패: ${error.message}\n다시 시도해주세요.`);
      }
    } else {
      console.log('⚠️ [숙박 정보 삭제] 로컬에만 저장된 정보 삭제 (서버 요청 없음)');
    }
  };

  // 체크리스트 항목 추가
  const handleAddChecklistItem = () => {
    if (!newChecklistItem.trim()) {
      return;
    }

    const checklistItemId = Date.now().toString();
    const checklistItem = {
      id: checklistItemId,
      text: newChecklistItem.trim(),
      completed: false,
      createdAt: new Date().toISOString()
    };

    const updatedChecklist = [...checklist, checklistItem];
    setChecklist(updatedChecklist);

    // localStorage에 저장
    const updatedPlan = {
      ...travelPlan,
      checklist: updatedChecklist
    };
    saveTravelPlan(updatedPlan);

    // 폼 초기화
    setNewChecklistItem('');
  };

  // 체크리스트 항목 완료 토글
  const handleToggleChecklistItem = (checklistItemId) => {
    const updatedChecklist = checklist.map(item =>
      item.id === checklistItemId
        ? { ...item, completed: !item.completed }
        : item
    );
    setChecklist(updatedChecklist);

    // localStorage에 저장
    const updatedPlan = {
      ...travelPlan,
      checklist: updatedChecklist
    };
    saveTravelPlan(updatedPlan);
  };

  // 체크리스트 항목 삭제
  const handleDeleteChecklistItem = (checklistItemId) => {
    const updatedChecklist = checklist.filter(item => item.id !== checklistItemId);
    setChecklist(updatedChecklist);

    // localStorage에 저장
    const updatedPlan = {
      ...travelPlan,
      checklist: updatedChecklist
    };
    saveTravelPlan(updatedPlan);
  };

  // 서버에서 새로고침
  const handleRefreshFromServer = async () => {
    if (!isAuthenticated) {
      alert('로그인이 필요합니다.');
      return;
    }

    const isLocalId = typeof planId === 'string' && planId.startsWith('local-');
    if (isLocalId) {
      alert('로컬에서만 만든 여행 계획입니다.\n먼저 "서버에 저장" 버튼을 눌러주세요.');
      return;
    }

    setIsRefreshing(true);
    try {
      console.log('🔄 [TravelDetailPage] 서버에서 새로고침 중... ID:', planId);
      const response = await travelApiService.getTravelPlan(planId);

      if (response.success && response.data) {
        const serverPlan = response.data;
        console.log('✅ [TravelDetailPage] 서버에서 불러온 여행 계획:', serverPlan);

        // 서버 데이터를 프론트엔드 형식으로 변환
        const convertDate = (isoDate) => {
          if (!isoDate) return '';
          return isoDate.split('T')[0];
        };

        // activities의 시간 변환
        const convertedDays = (serverPlan.days || []).map(day => ({
          ...day,
          activities: (day.activities || []).map(activity => ({
            ...activity,
            startTime: normalizeTime(activity.start_time || activity.startTime),
            endTime: normalizeTime(activity.end_time || activity.endTime),
          }))
        }));

        const plan = {
          id: serverPlan.id,
          title: serverPlan.title,
          destination: serverPlan.destination,
          startDate: convertDate(serverPlan.start_date),
          endDate: convertDate(serverPlan.end_date),
          duration: serverPlan.duration,
          budget: serverPlan.budget,
          description: serverPlan.description,
          createdAt: serverPlan.created_at,
          participants: serverPlan.participants || [],
          days: convertedDays,
          transportations: serverPlan.transportations || [],
          accommodations: serverPlan.accommodations || [],
          checklist: serverPlan.checklist || [],
          is_owner: serverPlan.is_owner,
          permission: serverPlan.permission,
        };

        // days 배열 자동 생성
        if (!plan.days || plan.days.length === 0) {
          if (plan.startDate && plan.duration) {
            const startDate = new Date(plan.startDate);
            plan.days = Array.from({ length: plan.duration }, (_, index) => ({
              date: new Date(startDate.getTime() + index * 24 * 60 * 60 * 1000)
                .toISOString()
                .split('T')[0],
              activities: []
            }));
          }
        }

        // 참여자 자동 추가
        if (!plan.participants || plan.participants.length === 0) {
          plan.participants = [{
            id: `me-${Date.now()}`,
            name: "나",
            contact: "",
            icon: "👤",
          }];
        }

        // localStorage 업데이트
        const savedPlans = localStorage.getItem("travelPlans");
        if (savedPlans) {
          const plans = JSON.parse(savedPlans);
          const existingIndex = plans.findIndex((p) => String(p.id) === String(planId));

          if (existingIndex >= 0) {
            plans[existingIndex] = plan;
          } else {
            plans.push(plan);
          }

          localStorage.setItem("travelPlans", JSON.stringify(plans));
        } else {
          localStorage.setItem("travelPlans", JSON.stringify([plan]));
        }

        // 상태 업데이트
        setTravelPlan(plan);
        setTransportations(plan.transportations || []);
        setAccommodations(plan.accommodations || []);
        setChecklist(plan.checklist || []);

        console.log('✅ [TravelDetailPage] 새로고침 완료');
        alert('✅ 서버에서 최신 정보를 불러왔습니다!');
      } else {
        throw new Error(response.message || '여행 계획을 불러올 수 없습니다.');
      }
    } catch (error) {
      console.error('❌ [TravelDetailPage] 서버 새로고침 실패:', error);
      alert(`서버 새로고침 실패: ${error.message}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  // 서버에 저장
  const handleSaveToServer = async () => {
    if (!isAuthenticated) {
      alert('로그인이 필요합니다.');
      return;
    }

    const confirmSave = window.confirm(
      '현재 여행 계획을 서버에 저장하시겠습니까?\n서버에 이미 동일한 계획이 있다면 덮어쓰여집니다.'
    );
    if (!confirmSave) return;

    setIsSaving(true);
    setShowMoreMenu(false); // 메뉴 닫기

    try {
      // JWT 토큰 가져오기
      const authToken = localStorage.getItem('jwtToken');
      if (!authToken) {
        throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
      }
      // 1. 여행 계획 기본 정보 저장/업데이트
      const planData = {
        title: travelPlan.title,
        destination: travelPlan.destination,
        startDate: travelPlan.startDate,
        endDate: travelPlan.endDate,
        budget: parseInt(travelPlan.budget) || 0,
        description: travelPlan.description || '',
      };

      let response;
      let serverPlanId;
      const isLocalId = typeof travelPlan.id === 'string' && travelPlan.id.startsWith('local-');

      if (isLocalId) {
        // 로컬 임시 ID이면 새로 생성
        console.log('🆕 [서버 저장] 새 여행 계획 생성 (로컬 ID:', travelPlan.id, ')');
        response = await travelApiService.createTravelPlan(planData);
      } else {
        // 서버 ID이면 업데이트
        console.log('🔄 [서버 저장] 기존 여행 계획 업데이트 (서버 ID:', travelPlan.id, ')');
        response = await travelApiService.updateTravelPlan(travelPlan.id, planData);
      }

      if (!response.success || !response.data || !response.data.id) {
        throw new Error('여행 계획 저장 실패');
      }

      serverPlanId = response.data.id;

      // 2. 참여자 저장
      console.log('💾 [서버 저장] 참여자 목록:', travelPlan.participants);
      if (travelPlan.participants && travelPlan.participants.length > 0) {
        for (const participant of travelPlan.participants) {
          const isLocalParticipantId = typeof participant.id === 'string' && participant.id.startsWith('local-');
          console.log('💾 [서버 저장] 참여자 처리 중:', participant.name, '(id:', participant.id, ')');

          try {
            if (!isLocalParticipantId) {
              // 서버 ID이면 업데이트
              console.log('🔄 [서버 저장] 참여자 업데이트:', participant.name);
              await travelApiService.updateParticipant(serverPlanId, participant.id, {
                name: participant.name,
                contact: participant.contact || '',
                icon: participant.icon || '👤',
              });
            } else {
              // 로컬 ID이면 새로 생성
              console.log('➕ [서버 저장] 참여자 생성:', participant.name);
              const participantResponse = await travelApiService.createParticipant(serverPlanId, {
                name: participant.name,
                contact: participant.contact || '',
                icon: participant.icon || '👤',
              });

              // 로컬 ID를 서버 ID로 교체
              if (participantResponse.success && participantResponse.data) {
                participant.id = participantResponse.data.id;
                console.log('✅ [서버 저장] 참여자 생성 완료:', participant.name, 'id:', participant.id);
              }
            }
          } catch (error) {
            console.error('❌ [서버 저장] 참여자 저장 실패:', participant.name, error);
            // 에러가 나도 계속 진행
          }
        }
      }

      // 3. 교통 정보 저장 (base64 이미지를 URL로 변환)
      if (transportations && transportations.length > 0) {
        console.log('🔄 [서버 저장] 교통 정보의 base64 이미지를 URL로 변환 중...');
        for (const transport of transportations) {
          const isLocalTransportId = typeof transport.id === 'string' && transport.id.startsWith('local-');

          // memo 필드의 base64 이미지를 URL로 변환
          const convertedTransport = await convertObjectBase64Images(
            transport,
            ['memo'],
            authToken
          );

          const transportData = {
            type: convertedTransport.type,
            reservationNumber: convertedTransport.reservationNumber || '',
            budget: parseInt(convertedTransport.budget) || 0,
            people: convertedTransport.people || [],
            memo: convertedTransport.memo || '',
          };

          if (!isLocalTransportId) {
            // 서버 ID이면 업데이트
            await travelApiService.updateTransportation(serverPlanId, transport.id, transportData);
          } else {
            // 로컬 ID이면 새로 생성
            const transportResponse = await travelApiService.createTransportation(serverPlanId, transportData);

            // 로컬 ID를 서버 ID로 교체
            if (transportResponse.success && transportResponse.data) {
              transport.id = transportResponse.data.id;
              console.log('✅ [서버 저장] 교통 정보 생성 완료, id:', transport.id);
            }
          }

          // 변환된 memo를 원본 객체에도 반영 (localStorage 저장용)
          transport.memo = convertedTransport.memo;
        }
      }

      // 4. 숙박 정보 저장 (base64 이미지를 URL로 변환)
      if (accommodations && accommodations.length > 0) {
        console.log('🔄 [서버 저장] 숙박 정보의 base64 이미지를 URL로 변환 중...');
        for (const accommodation of accommodations) {
          const isLocalAccommodationId = typeof accommodation.id === 'string' && accommodation.id.startsWith('local-');

          // memo 필드의 base64 이미지를 URL로 변환
          const convertedAccommodation = await convertObjectBase64Images(
            accommodation,
            ['memo'],
            authToken
          );

          const accommodationData = {
            name: convertedAccommodation.name,
            address: convertedAccommodation.address || '',
            reservationNumber: convertedAccommodation.reservationNumber || '',
            budget: parseInt(convertedAccommodation.budget) || 0,
            people: convertedAccommodation.people || [],
            memo: convertedAccommodation.memo || '',
          };

          if (!isLocalAccommodationId) {
            // 서버 ID이면 업데이트
            await travelApiService.updateAccommodation(serverPlanId, accommodation.id, accommodationData);
          } else {
            // 로컬 ID이면 새로 생성
            const accommodationResponse = await travelApiService.createAccommodation(serverPlanId, accommodationData);

            // 로컬 ID를 서버 ID로 교체
            if (accommodationResponse.success && accommodationResponse.data) {
              accommodation.id = accommodationResponse.data.id;
              console.log('✅ [서버 저장] 숙박 정보 생성 완료, id:', accommodation.id);
            }
          }

          // 변환된 memo를 원본 객체에도 반영 (localStorage 저장용)
          accommodation.memo = convertedAccommodation.memo;
        }
      }

      // 5. 체크리스트 저장
      if (checklist && checklist.length > 0) {
        for (const item of checklist) {
          const isLocalChecklistId = typeof item.id === 'string' && item.id.startsWith('local-');

          if (!isLocalChecklistId) {
            // 서버 ID이면 스킵 (체크리스트는 toggle만 있고 update API는 없음)
            continue;
          } else {
            // 로컬 ID이면 새로 생성
            const checklistResponse = await travelApiService.createChecklistItem(serverPlanId, {
              text: item.text,
              completed: item.completed || false,
            });

            // 로컬 ID를 서버 ID로 교체
            if (checklistResponse.success && checklistResponse.data) {
              item.id = checklistResponse.data.id;
              console.log('✅ [서버 저장] 체크리스트 생성 완료, id:', item.id);
            }
          }
        }
      }

      // 6. 일별 활동 저장 (base64 이미지를 URL로 변환)
      if (travelPlan.days && travelPlan.days.length > 0) {
        console.log('🔄 [서버 저장] 활동의 base64 이미지를 URL로 변환 중...');
        for (const day of travelPlan.days) {
          if (day.activities && day.activities.length > 0) {
            const dayIndex = travelPlan.days.indexOf(day);
            const dayId = dayIndex + 1; // 서버에서 dayId는 1부터 시작한다고 가정

            for (let i = 0; i < day.activities.length; i++) {
              const activity = day.activities[i];
              const isLocalActivityId = typeof activity.id === 'string' && activity.id.startsWith('local-');

              // description 필드의 base64 이미지를 URL로 변환
              const convertedActivity = await convertObjectBase64Images(
                activity,
                ['description'],
                authToken
              );

              const activityData = {
                type: 'duration', // duration 타입 사용
                title: convertedActivity.title,
                duration: parseInt(convertedActivity.duration) || 0, // 분 단위 정수
                location: convertedActivity.location || '',
                budget: parseInt(convertedActivity.budget) || 0,
                people: convertedActivity.people || [],
                description: convertedActivity.description || '',
                category: convertedActivity.category || '',
                // endTime은 서버가 자동 계산
              };

              // startTime이 명시적으로 있고 dayStartTime과 다를 때만 포함
              if (activity.startTime && activity.startTime !== dayStartTime) {
                activityData.startTime = normalizeTime(activity.startTime);
              }

              if (!isLocalActivityId) {
                // 서버 ID이면 업데이트
                await travelApiService.updateActivity(serverPlanId, dayId, activity.id, activityData);
              } else {
                // 로컬 ID이면 새로 생성
                const activityResponse = await travelApiService.createActivity(serverPlanId, dayId, activityData);

                // 로컬 ID를 서버 ID로 교체
                if (activityResponse.success && activityResponse.data) {
                  activity.id = activityResponse.data.id;
                  console.log('✅ [서버 저장] 활동 생성 완료, id:', activity.id);
                }
              }

              // 변환된 description을 원본 객체에도 반영 (localStorage 저장용)
              activity.description = convertedActivity.description;
            }
          }
        }
      }

      // 7. localStorage에 저장 (로컬 ID를 서버 ID로 교체)
      const originalLocalId = travelPlan.id; // 원래 로컬 ID 저장
      const updatedPlan = {
        ...travelPlan,
        id: serverPlanId, // 로컬 임시 ID를 서버 ID로 교체
        transportations: transportations, // ID가 교체된 교통 정보
        accommodations: accommodations, // ID가 교체된 숙박 정보
        checklist: checklist, // ID가 교체된 체크리스트
        // days는 이미 travelPlan에 포함되어 있고, activities의 ID도 교체됨
      };
      saveTravelPlan(updatedPlan, originalLocalId); // 원래 로컬 ID를 전달
      setTravelPlan(updatedPlan);
      setTransportations(transportations); // 상태도 업데이트
      setAccommodations(accommodations);
      setChecklist(checklist);

      // 8. URL이 로컬 ID였다면 서버 ID로 리다이렉트
      if (isLocalId && planId !== serverPlanId.toString()) {
        console.log('🔄 [서버 저장] URL 리다이렉트:', planId, '→', serverPlanId);
        navigate(`/travel/${serverPlanId}`, { replace: true });
      }

      alert('✅ 서버에 성공적으로 저장되었습니다!');
    } catch (error) {
      console.error('❌ [TravelDetailPage] 서버 저장 실패:', error);
      alert(`서버 저장 실패: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // 교통 정보 참여자 토글
  const handleTransportParticipantToggle = (participantId) => {
    const currentPeople = Array.isArray(newTransport.people) ? newTransport.people : [];
    const updatedPeople = currentPeople.includes(participantId)
      ? currentPeople.filter(id => id !== participantId)
      : [...currentPeople, participantId];
    setNewTransport({ ...newTransport, people: updatedPeople });
  };

  // 교통 정보 전체 참여자 선택/해제
  const handleTransportSelectAll = () => {
    const allParticipantIds = travelPlan?.participants?.map(p => p.id) || [];
    const currentPeople = Array.isArray(newTransport.people) ? newTransport.people : [];
    const updatedPeople = currentPeople.length === allParticipantIds.length ? [] : allParticipantIds;
    setNewTransport({ ...newTransport, people: updatedPeople });
  };

  // 숙박 정보 참여자 토글
  const handleAccommodationParticipantToggle = (participantId) => {
    const currentPeople = Array.isArray(newAccommodation.people) ? newAccommodation.people : [];
    const updatedPeople = currentPeople.includes(participantId)
      ? currentPeople.filter(id => id !== participantId)
      : [...currentPeople, participantId];
    setNewAccommodation({ ...newAccommodation, people: updatedPeople });
  };

  // 숙박 정보 전체 참여자 선택/해제
  const handleAccommodationSelectAll = () => {
    const allParticipantIds = travelPlan?.participants?.map(p => p.id) || [];
    const currentPeople = Array.isArray(newAccommodation.people) ? newAccommodation.people : [];
    const updatedPeople = currentPeople.length === allParticipantIds.length ? [] : allParticipantIds;
    setNewAccommodation({ ...newAccommodation, people: updatedPeople });
  };

  if (!travelPlan) {
    return (
      <div className="travel-detail-loading">
        <div className="loading-spinner"></div>
        <p>여행 계획을 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="travel-detail-page">
      {/* 앱 바 헤더 */}
      <div className="travel-detail-header app-bar">
        <button className="back-btn" onClick={() => navigate("/travel")}>
          ←
        </button>
        <div className="app-bar-title-container">
          <button
            className="nav-arrow-btn prev"
            onClick={() => {
              setCurrentPageType('checklist');
              setShowTravelInfoModal(true);
            }}
            title="체크리스트"
          >
            ✅
          </button>
          <h1
            className="app-bar-title"
            onClick={() => {
              setCurrentPageType('info');
              setShowTravelInfoModal(true);
            }}
            style={{ cursor: 'pointer' }}
          >
            {travelPlan.title}
          </h1>
          <button
            className="nav-arrow-btn next"
            onClick={() => {
              setCurrentPageType('booking');
              setShowTravelInfoModal(true);
            }}
            title="예약 정보"
          >
            🎫
          </button>
        </div>
        <div className="more-btn-container" ref={moreMenuRef}>
          <button
            className="more-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowMoreMenu(!showMoreMenu);
            }}
            title="더보기"
          >
            ⋮
          </button>
          {showMoreMenu && (
            <div className="more-menu-dropdown-appbar">
              <button
                className="menu-item-appbar"
                onClick={() => handleSelectPage('info')}
              >
                ✈️ 여행 정보
              </button>
              <button
                className="menu-item-appbar"
                onClick={() => handleSelectPage('booking')}
              >
                🏨 예약 정보
              </button>
              <button
                className="menu-item-appbar"
                onClick={() => handleSelectPage('checklist')}
              >
                ✓ 체크 리스트
              </button>
              {isAuthenticated && (
                <>
                  <div className="menu-divider-appbar"></div>
                  <button
                    className="menu-item-appbar"
                    onClick={() => {
                      setShowShareModal(true);
                      setShowMoreMenu(false);
                    }}
                  >
                    🔗 공유하기
                  </button>
                  <button
                    className="menu-item-appbar"
                    onClick={() => {
                      setShowShareListModal(true);
                      setShowMoreMenu(false);
                    }}
                  >
                    📋 공유 관리
                  </button>
                  <button
                    className="menu-item-appbar save-to-server"
                    onClick={handleSaveToServer}
                    disabled={isSaving}
                  >
                    {isSaving ? '💾 저장 중...' : '💾 서버에 저장'}
                  </button>
                  <button
                    className="menu-item-appbar"
                    onClick={handleRefreshFromServer}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? '🔄 새로고침 중...' : '🔄 서버에서 새로고침'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="travel-detail-container">

        {/* 통합 모달 - 여행 정보 / 예약 정보 / 체크 리스트 */}
        {showTravelInfoModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowTravelInfoModal(false)}
          >
            <div
              className="modal-content travel-info-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{getPageTitle(currentPageType)}</h2>
                <div style={{ display: "flex", gap: "10px" }}>
                  {/* 여행 정보 페이지일 때만 편집 버튼 표시 */}
                  {currentPageType === 'info' && (
                    <>
                      {!isEditingTravelInfo ? (
                        <button
                          className="edit-travel-info-btn"
                          onClick={handleStartEditTravelInfo}
                        >
                          ✏️ 편집
                        </button>
                      ) : (
                        <>
                          <button
                            className="save-travel-info-btn"
                            onClick={handleSaveTravelInfo}
                          >
                            ✓ 저장
                          </button>
                          <button
                            className="cancel-travel-info-btn"
                            onClick={handleCancelEditTravelInfo}
                          >
                            ✕ 취소
                          </button>
                        </>
                      )}
                    </>
                  )}
                  <button
                    className="modal-close-btn"
                    onClick={() => {
                      setShowTravelInfoModal(false);
                      setIsEditingTravelInfo(false);
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="modal-body">
                {/* 여행 정보 페이지 */}
                {currentPageType === 'info' && (
                  <>
                    {!isEditingTravelInfo ? (
                      // 읽기 모드
                      <div className="info-section">
                    <div className="info-row">
                      <span className="info-label">✈️ 제목</span>
                      <span className="info-value">{travelPlan.title}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">📍 목적지</span>
                      <span className="info-value">{travelPlan.destination}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">📅 여행 기간</span>
                      <span className="info-value">
                        {formatDate(travelPlan.startDate)} ~{" "}
                        {formatDate(travelPlan.endDate)}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">⏰ 일수</span>
                      <span className="info-value">{travelPlan.duration}일</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">💰 계획 예산</span>
                      <span className="info-value">
                        {(travelPlan.budget || 0).toLocaleString()}원
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">💵 실제 사용 예산</span>
                      <span className="info-value">
                        {calculateTotalBudget().toLocaleString()}원
                      </span>
                    </div>
                    {/* 예산 세부 항목 */}
                    {calculateTotalBudget() > 0 && (
                      <div className="info-row budget-breakdown" style={{ fontSize: '13px', color: '#666', marginLeft: '20px' }}>
                        <div>
                          {travelPlan.days && travelPlan.days.reduce((total, day) => total + calculateDayBudget(day?.activities), 0) > 0 && (
                            <div>• 활동: {travelPlan.days.reduce((total, day) => total + calculateDayBudget(day?.activities), 0).toLocaleString()}원</div>
                          )}
                          {transportations.reduce((total, t) => total + (parseInt(t.budget) || 0), 0) > 0 && (
                            <div>• 교통: {transportations.reduce((total, t) => total + (parseInt(t.budget) || 0), 0).toLocaleString()}원</div>
                          )}
                          {accommodations.reduce((total, a) => total + (parseInt(a.budget) || 0), 0) > 0 && (
                            <div>• 숙박: {accommodations.reduce((total, a) => total + (parseInt(a.budget) || 0), 0).toLocaleString()}원</div>
                          )}
                        </div>
                      </div>
                    )}
                    {travelPlan.description && (
                      <div className="info-row description-row">
                        <span className="info-label">📝 설명</span>
                        <p className="info-value description-value">
                          {travelPlan.description}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  // 편집 모드
                  <div className="info-section edit-mode">
                    <div className="form-group">
                      <label>✈️ 제목</label>
                      <input
                        type="text"
                        value={editedTravelInfo.title}
                        onChange={(e) =>
                          setEditedTravelInfo({
                            ...editedTravelInfo,
                            title: e.target.value,
                          })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSaveTravelInfo();
                          }
                        }}
                        placeholder="여행 제목을 입력하세요"
                      />
                    </div>
                    <div className="form-group">
                      <label>📍 목적지</label>
                      <input
                        type="text"
                        value={editedTravelInfo.destination}
                        onChange={(e) =>
                          setEditedTravelInfo({
                            ...editedTravelInfo,
                            destination: e.target.value,
                          })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSaveTravelInfo();
                          }
                        }}
                        placeholder="목적지를 입력하세요"
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>📅 시작 날짜</label>
                        <input
                          type="date"
                          value={editedTravelInfo.startDate}
                          onChange={(e) =>
                            setEditedTravelInfo({
                              ...editedTravelInfo,
                              startDate: e.target.value,
                            })
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSaveTravelInfo();
                            }
                          }}
                        />
                      </div>
                      <div className="form-group">
                        <label>📅 종료 날짜</label>
                        <input
                          type="date"
                          value={editedTravelInfo.endDate}
                          onChange={(e) =>
                            setEditedTravelInfo({
                              ...editedTravelInfo,
                              endDate: e.target.value,
                            })
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSaveTravelInfo();
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>💰 계획 예산 (원)</label>
                      <input
                        type="number"
                        value={editedTravelInfo.budget}
                        onChange={(e) =>
                          setEditedTravelInfo({
                            ...editedTravelInfo,
                            budget: e.target.value,
                          })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSaveTravelInfo();
                          }
                        }}
                        placeholder="예산을 입력하세요"
                      />
                    </div>
                    <div className="form-group">
                      <label>📝 설명</label>
                      <textarea
                        value={editedTravelInfo.description}
                        onChange={(e) =>
                          setEditedTravelInfo({
                            ...editedTravelInfo,
                            description: e.target.value,
                          })
                        }
                        placeholder="여행 설명을 입력하세요"
                        rows="4"
                      />
                    </div>
                  </div>
                )}

                {/* 참여자 관리 섹션 */}
                <div className="info-section">
                  <div className="section-header">
                    <h3>
                      👥 참여자 ({travelPlan.participants?.length || 0}명)
                    </h3>
                    {isEditingTravelInfo && (
                      <button
                        className="manage-participants-btn"
                        onClick={() =>
                          setShowParticipantManagement(!showParticipantManagement)
                        }
                      >
                        {showParticipantManagement ? "닫기" : "관리"}
                      </button>
                    )}
                  </div>

                  {/* 참여자 목록 (간단 표시) */}
                  {travelPlan?.participants &&
                    travelPlan.participants.length > 0 &&
                    !showParticipantManagement && (
                      <div className="participants-preview">
                        {travelPlan.participants.map((participant) => (
                          <span
                            key={participant.id}
                            className="participant-chip"
                          >
                            <span className="participant-icon">{participant.icon || '👤'}</span>
                            {participant.name}
                          </span>
                        ))}
                      </div>
                    )}

                  {/* 참여자 관리 패널 */}
                  {showParticipantManagement && (
                    <div className="participant-management-panel">
                      {/* 참여자 목록 */}
                      {travelPlan?.participants &&
                      travelPlan.participants.length > 0 ? (
                        <div className="participants-list-detail">
                          {travelPlan.participants.map((participant) => (
                            <div
                              key={participant.id}
                              className="participant-item-detail"
                            >
                              <div className="participant-info-detail">
                                <div className="participant-name-detail">
                                  <span className="participant-icon">{participant.icon || '👤'}</span>
                                  {participant.name}
                                </div>
                                {participant.contact && (
                                  <div className="participant-contact-detail">
                                    {participant.contact}
                                  </div>
                                )}
                              </div>
                              <button
                                className="remove-participant-btn-detail"
                                onClick={() =>
                                  handleRemoveParticipant(participant.id)
                                }
                                title="참여자 삭제"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-participants-detail">
                          아직 추가된 참여자가 없습니다.
                        </div>
                      )}

                      <button
                        className="add-participant-btn-detail"
                        onClick={() =>
                          setShowAddParticipant(!showAddParticipant)
                        }
                        style={{ width: "100%", marginTop: "10px" }}
                      >
                        {showAddParticipant ? "취소" : "+ 참여자 추가"}
                      </button>

                      {/* 참여자 추가 폼 */}
                      {showAddParticipant && (
                        <div
                          className="add-participant-form-detail"
                          style={{ marginTop: "10px" }}
                        >
                          {/* 아이콘 선택 */}
                          <div className="icon-selector-container">
                            <label className="icon-selector-label">아이콘 선택</label>
                            <div className="icon-selector-grid">
                              {availableIcons.map((icon, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  className={`icon-option ${newParticipant.icon === icon ? 'selected' : ''}`}
                                  onClick={() => setNewParticipant({...newParticipant, icon: icon})}
                                >
                                  {icon}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="participant-form-row-detail">
                            <input
                              type="text"
                              placeholder="이름 *"
                              value={newParticipant.name}
                              onChange={(e) =>
                                setNewParticipant({
                                  ...newParticipant,
                                  name: e.target.value,
                                })
                              }
                              className="participant-name-input-detail"
                            />
                            <input
                              type="text"
                              placeholder="이메일 또는 전화번호"
                              value={newParticipant.contact}
                              onChange={(e) =>
                                setNewParticipant({
                                  ...newParticipant,
                                  contact: e.target.value,
                                })
                              }
                              className="participant-contact-input-detail"
                            />
                          </div>
                          <div className="participant-form-actions-detail">
                            <button
                              className="participant-save-btn-detail"
                              onClick={handleAddParticipant}
                            >
                              추가
                            </button>
                            <button
                              className="participant-cancel-btn-detail"
                              onClick={() => {
                                setShowAddParticipant(false);
                                setNewParticipant({ name: "", contact: "" });
                              }}
                            >
                              취소
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 청구 목록 섹션 */}
                <div className="info-section">
                  <div className="section-header">
                    <h3>💰 청구 목록</h3>
                    <button
                      className="manage-participants-btn"
                      onClick={() => setShowBillingModal(true)}
                    >
                      자세히 보기
                    </button>
                  </div>

                  {/* 청구 목록 요약 */}
                  <div style={{ marginTop: "10px" }}>
                    {Object.keys(calculateBillingList).length > 0 ? (
                      Object.values(calculateBillingList).map((billing) => (
                        <div
                          key={billing.id}
                          className="info-row"
                          style={{ padding: "8px 0" }}
                        >
                          <span className="info-label">{billing.name}</span>
                          <span className="info-value" style={{ fontWeight: "600" }}>
                            {billing.totalAmount.toLocaleString()}원
                          </span>
                        </div>
                      ))
                    ) : (
                      <div style={{ textAlign: "center", color: "#999", fontStyle: "italic", padding: "15px" }}>
                        청구할 항목이 없습니다.
                      </div>
                    )}
                  </div>
                </div>
                  </>
                )}

                {/* 예약 정보 페이지 */}
                {currentPageType === 'booking' && (
                  <div className="booking-page-content">
                    {/* 교통 정보 섹션 */}
                    <div className="info-section">
                      <div className="section-header">
                        <h3>🚗 교통 정보</h3>
                        <button
                          className="add-booking-btn"
                          onClick={() => setShowAddTransport(!showAddTransport)}
                        >
                          {showAddTransport ? '취소' : '+ 추가'}
                        </button>
                      </div>

                      {/* 교통 정보 목록 */}
                      {transportations.length > 0 && (
                        <div className="booking-items-list">
                          {transportations.map((transport) => (
                            <div key={transport.id} className="booking-item-card">
                              <div className="booking-item-header">
                                <h4>{transport.type}</h4>
                                <div className="booking-item-actions">
                                  <button
                                    className="edit-booking-btn"
                                    onClick={() => handleEditTransport(transport)}
                                    title="수정"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    className="delete-booking-btn"
                                    onClick={() => handleDeleteTransport(transport.id)}
                                    title="삭제"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                              <div className="booking-item-body">
                                {transport.reservationNumber && (
                                  <div className="booking-field">
                                    <span className="field-label">예약번호:</span>
                                    <span className="field-value">{transport.reservationNumber}</span>
                                  </div>
                                )}
                                {transport.budget > 0 && (
                                  <div className="booking-field">
                                    <span className="field-label">예산:</span>
                                    <span className="field-value">{transport.budget.toLocaleString()}원</span>
                                  </div>
                                )}
                                {transport.participants && transport.participants.length > 0 && (
                                  <div className="booking-field">
                                    <span className="field-label">참여자:</span>
                                    <span className="field-value">
                                      {/* 중복 제거: ID 기준으로 고유한 참여자만 표시 */}
                                      {Array.from(new Map(transport.participants.map(p => [p.id, p])).values()).map(participant => (
                                        <span key={participant.id} className="participant-tag">
                                          {participant.icon || '👤'} {participant.name}
                                        </span>
                                      ))}
                                    </span>
                                  </div>
                                )}
                                {/* 이전 people 필드(ID 배열) 호환성 유지 */}
                                {!transport.participants && transport.people && transport.people.length > 0 && (
                                  <div className="booking-field">
                                    <span className="field-label">참여자:</span>
                                    <span className="field-value">
                                      {transport.people.map(participantId => {
                                        const participant = travelPlan.participants?.find(p => p.id === participantId);
                                        return participant ? (
                                          <span key={participantId} className="participant-tag">
                                            {participant.icon || '👤'} {participant.name}
                                          </span>
                                        ) : null;
                                      })}
                                    </span>
                                  </div>
                                )}
                                {transport.memo && (
                                  <div className="booking-memo">
                                    <div dangerouslySetInnerHTML={{ __html: convertImageUrlsForProxy(transport.memo) }} />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* 교통 정보 추가/수정 폼 */}
                      {(showAddTransport || editingTransportId) && (
                        <div className="booking-add-form">
                          <div className="form-group">
                            <label>교통 수단 *</label>
                            <input
                              type="text"
                              placeholder="예: KTX, 버스, 렌터카"
                              value={newTransport.type}
                              onChange={(e) => setNewTransport({...newTransport, type: e.target.value})}
                              className="booking-input"
                            />
                          </div>
                          <div className="form-group">
                            <label>예산</label>
                            <input
                              type="text"
                              placeholder="금액을 입력하세요 (원)"
                              value={newTransport.budget}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                setNewTransport({...newTransport, budget: value});
                              }}
                              className="booking-input"
                            />
                          </div>
                          <div className="input-group participants-group">
                            <div className="participants-header">
                              <label className="input-label">
                                참여자 (
                                {Array.isArray(newTransport.people)
                                  ? newTransport.people.length
                                  : 0}
                                명)
                              </label>
                              {travelPlan?.participants &&
                                travelPlan.participants.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={handleTransportSelectAll}
                                    className="select-all-toggle-btn"
                                  >
                                    {Array.isArray(newTransport.people) &&
                                    newTransport.people.length ===
                                      travelPlan.participants.length
                                      ? "전체 해제"
                                      : "전체 선택"}
                                  </button>
                                )}
                            </div>

                            {/* 참여자가 등록되어 있는 경우 */}
                            {travelPlan?.participants &&
                            travelPlan.participants.length > 0 ? (
                              <div className="participants-toggle-container">
                                {travelPlan.participants.map((participant) => (
                                  <button
                                    key={participant.id}
                                    type="button"
                                    className={`participant-toggle-label ${
                                      Array.isArray(newTransport.people) &&
                                      newTransport.people.includes(participant.id)
                                        ? "selected"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      handleTransportParticipantToggle(participant.id)
                                    }
                                  >
                                    {participant.name}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              /* 참여자가 등록되지 않은 경우 비활성화된 입력 */
                              <input
                                type="text"
                                value="등록된 참여자가 없습니다."
                                className="people-input disabled"
                                disabled
                                readOnly
                                style={{ pointerEvents: "none" }}
                              />
                            )}
                          </div>
                          <div className="form-group">
                            <label>메모</label>
                            <RichTextEditor
                              value={newTransport.memo}
                              onChange={(content) => setNewTransport({...newTransport, memo: content})}
                              placeholder="추가 정보를 입력하세요 (이미지는 Ctrl+V로 붙여넣기)"
                              height="150px"
                              localMode={!isAuthenticated}
                            />
                          </div>
                          <div className="form-actions">
                            {editingTransportId ? (
                              <>
                                <button className="save-btn" onClick={handleUpdateTransport}>
                                  ✓ 수정 완료
                                </button>
                                <button className="cancel-btn" onClick={handleCancelEditTransport}>
                                  ✕ 취소
                                </button>
                              </>
                            ) : (
                              <>
                                <button className="save-btn" onClick={handleAddTransport}>
                                  ✓ 저장
                                </button>
                                <button className="cancel-btn" onClick={() => {
                                  setShowAddTransport(false);
                                  setNewTransport({ type: '', reservationNumber: '', budget: '', people: [], memo: '' });
                                }}>
                                  ✕ 취소
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {transportations.length === 0 && !showAddTransport && (
                        <div className="booking-empty">
                          <p>아직 등록된 교통 정보가 없습니다.</p>
                        </div>
                      )}
                    </div>

                    {/* 숙박 정보 섹션 */}
                    <div className="info-section">
                      <div className="section-header">
                        <h3>🏨 숙박 정보</h3>
                        <button
                          className="add-booking-btn"
                          onClick={() => setShowAddAccommodation(!showAddAccommodation)}
                        >
                          {showAddAccommodation ? '취소' : '+ 추가'}
                        </button>
                      </div>

                      {/* 숙박 정보 목록 */}
                      {accommodations.length > 0 && (
                        <div className="booking-items-list">
                          {accommodations.map((accommodation) => (
                            <div key={accommodation.id} className="booking-item-card">
                              <div className="booking-item-header">
                                <h4>{accommodation.name}</h4>
                                <div className="booking-item-actions">
                                  <button
                                    className="edit-booking-btn"
                                    onClick={() => handleEditAccommodation(accommodation)}
                                    title="수정"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    className="delete-booking-btn"
                                    onClick={() => handleDeleteAccommodation(accommodation.id)}
                                    title="삭제"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                              <div className="booking-item-body">
                                {accommodation.address && (
                                  <div className="booking-field">
                                    <span className="field-label">주소:</span>
                                    <span className="field-value">{accommodation.address}</span>
                                  </div>
                                )}
                                {accommodation.reservationNumber && (
                                  <div className="booking-field">
                                    <span className="field-label">예약번호:</span>
                                    <span className="field-value">{accommodation.reservationNumber}</span>
                                  </div>
                                )}
                                {accommodation.budget > 0 && (
                                  <div className="booking-field">
                                    <span className="field-label">예산:</span>
                                    <span className="field-value">{accommodation.budget.toLocaleString()}원</span>
                                  </div>
                                )}
                                {accommodation.participants && accommodation.participants.length > 0 && (
                                  <div className="booking-field">
                                    <span className="field-label">참여자:</span>
                                    <span className="field-value">
                                      {/* 중복 제거: ID 기준으로 고유한 참여자만 표시 */}
                                      {Array.from(new Map(accommodation.participants.map(p => [p.id, p])).values()).map(participant => (
                                        <span key={participant.id} className="participant-tag">
                                          {participant.icon || '👤'} {participant.name}
                                        </span>
                                      ))}
                                    </span>
                                  </div>
                                )}
                                {/* 이전 people 필드(ID 배열) 호환성 유지 */}
                                {!accommodation.participants && accommodation.people && accommodation.people.length > 0 && (
                                  <div className="booking-field">
                                    <span className="field-label">참여자:</span>
                                    <span className="field-value">
                                      {accommodation.people.map(participantId => {
                                        const participant = travelPlan.participants?.find(p => p.id === participantId);
                                        return participant ? (
                                          <span key={participantId} className="participant-tag">
                                            {participant.icon || '👤'} {participant.name}
                                          </span>
                                        ) : null;
                                      })}
                                    </span>
                                  </div>
                                )}
                                {accommodation.memo && (
                                  <div className="booking-memo">
                                    <div dangerouslySetInnerHTML={{ __html: convertImageUrlsForProxy(accommodation.memo) }} />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* 숙박 정보 추가/수정 폼 */}
                      {(showAddAccommodation || editingAccommodationId) && (
                        <div className="booking-add-form">
                          <div className="form-group">
                            <label>숙소명 *</label>
                            <input
                              type="text"
                              placeholder="숙소 이름을 입력하세요"
                              value={newAccommodation.name}
                              onChange={(e) => setNewAccommodation({...newAccommodation, name: e.target.value})}
                              className="booking-input"
                            />
                          </div>
                          <div className="form-group">
                            <label>예산</label>
                            <input
                              type="text"
                              placeholder="금액을 입력하세요 (원)"
                              value={newAccommodation.budget}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                setNewAccommodation({...newAccommodation, budget: value});
                              }}
                              className="booking-input"
                            />
                          </div>
                          <div className="input-group participants-group">
                            <div className="participants-header">
                              <label className="input-label">
                                참여자 (
                                {Array.isArray(newAccommodation.people)
                                  ? newAccommodation.people.length
                                  : 0}
                                명)
                              </label>
                              {travelPlan?.participants &&
                                travelPlan.participants.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={handleAccommodationSelectAll}
                                    className="select-all-toggle-btn"
                                  >
                                    {Array.isArray(newAccommodation.people) &&
                                    newAccommodation.people.length ===
                                      travelPlan.participants.length
                                      ? "전체 해제"
                                      : "전체 선택"}
                                  </button>
                                )}
                            </div>

                            {/* 참여자가 등록되어 있는 경우 */}
                            {travelPlan?.participants &&
                            travelPlan.participants.length > 0 ? (
                              <div className="participants-toggle-container">
                                {travelPlan.participants.map((participant) => (
                                  <button
                                    key={participant.id}
                                    type="button"
                                    className={`participant-toggle-label ${
                                      Array.isArray(newAccommodation.people) &&
                                      newAccommodation.people.includes(participant.id)
                                        ? "selected"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      handleAccommodationParticipantToggle(participant.id)
                                    }
                                  >
                                    {participant.name}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              /* 참여자가 등록되지 않은 경우 비활성화된 입력 */
                              <input
                                type="text"
                                value="등록된 참여자가 없습니다."
                                className="people-input disabled"
                                disabled
                                readOnly
                                style={{ pointerEvents: "none" }}
                              />
                            )}
                          </div>
                          <div className="form-group">
                            <label>메모</label>
                            <RichTextEditor
                              value={newAccommodation.memo}
                              onChange={(content) => setNewAccommodation({...newAccommodation, memo: content})}
                              placeholder="추가 정보를 입력하세요 (이미지는 Ctrl+V로 붙여넣기)"
                              height="150px"
                              localMode={!isAuthenticated}
                            />
                          </div>
                          <div className="form-actions">
                            {editingAccommodationId ? (
                              <>
                                <button className="save-btn" onClick={handleUpdateAccommodation}>
                                  ✓ 수정 완료
                                </button>
                                <button className="cancel-btn" onClick={handleCancelEditAccommodation}>
                                  ✕ 취소
                                </button>
                              </>
                            ) : (
                              <>
                                <button className="save-btn" onClick={handleAddAccommodation}>
                                  ✓ 저장
                                </button>
                                <button className="cancel-btn" onClick={() => {
                                  setShowAddAccommodation(false);
                                  setNewAccommodation({ name: '', address: '', reservationNumber: '', budget: '', people: [], memo: '' });
                                }}>
                                  ✕ 취소
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {accommodations.length === 0 && !showAddAccommodation && (
                        <div className="booking-empty">
                          <p>아직 등록된 숙박 정보가 없습니다.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 체크 리스트 페이지 */}
                {currentPageType === 'checklist' && (
                  <div className="checklist-page-content">
                    <div className="info-section">
                      <div className="checklist-input-container">
                        <input
                          type="text"
                          placeholder="새로운 체크리스트 항목을 입력하세요..."
                          value={newChecklistItem}
                          onChange={(e) => setNewChecklistItem(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newChecklistItem.trim()) {
                              handleAddChecklistItem();
                            }
                          }}
                          className="checklist-input"
                        />
                        <button
                          className="add-checklist-btn"
                          onClick={handleAddChecklistItem}
                        >
                          + 추가
                        </button>
                      </div>

                      {/* 체크리스트 항목 목록 */}
                      <div className="checklist-items">
                        {checklist.length > 0 ? (
                          checklist.map((item) => (
                            <div key={item.id} className="checklist-item">
                              <input
                                type="checkbox"
                                checked={item.completed}
                                onChange={() => handleToggleChecklistItem(item.id)}
                                className="checklist-checkbox"
                              />
                              <span className={`checklist-item-text ${item.completed ? 'completed' : ''}`}>
                                {item.text}
                              </span>
                              <button
                                className="checklist-item-delete"
                                onClick={() => handleDeleteChecklistItem(item.id)}
                                title="삭제"
                              >
                                ✕
                              </button>
                            </div>
                          ))
                        ) : (
                          <p style={{ color: '#999', fontStyle: 'italic', padding: '40px', textAlign: 'center' }}>
                            아직 체크리스트가 비어있습니다.<br />
                            위에서 항목을 추가해보세요!
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 청구 목록 상세 모달 */}
        {showBillingModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowBillingModal(false)}
          >
            <div
              className="modal-content travel-info-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>💰 청구 목록 상세</h2>
                <button
                  className="modal-close-btn"
                  onClick={() => setShowBillingModal(false)}
                >
                  ✕
                </button>
              </div>

              <div className="modal-body">
                {Object.keys(calculateBillingList).length > 0 ? (
                  Object.values(calculateBillingList).map((billing) => (
                    <div key={billing.id} className="info-section">
                      <div className="section-header">
                        <h3>
                          {billing.name} - 총 {billing.totalAmount.toLocaleString()}원
                        </h3>
                      </div>

                      {/* 활동 목록 */}
                      <div style={{ marginTop: "10px" }}>
                        {billing.activities.map((activity, index) => (
                          <div
                            key={index}
                            style={{
                              background: "#f8f9fa",
                              padding: "12px",
                              borderRadius: "8px",
                              marginBottom: "8px",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "6px",
                              }}
                            >
                              <div style={{ fontWeight: "600", color: "#333" }}>
                                {activity.day ? `Day ${activity.day}` : '예약'} - {activity.title}
                              </div>
                              <div style={{ fontWeight: "600", color: "#667eea" }}>
                                {activity.sharedAmount.toLocaleString()}원
                              </div>
                            </div>
                            <div style={{ fontSize: "12px", color: "#666" }}>
                              총 예산: {activity.budget.toLocaleString()}원
                              {activity.participantsCount > 0 && (
                                <> / {activity.participantsCount}명 분할</>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: "center", color: "#999", fontStyle: "italic", padding: "40px" }}>
                    청구할 항목이 없습니다.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 일자 탭 */}
        <div className="day-tabs">
          {travelPlan.days && travelPlan.days.map((day, index) => (
            <button
              key={index}
              className={`day-tab ${selectedDay === index ? "active" : ""}`}
              onClick={() => setSelectedDay(index)}
            >
              <div className="day-number">Day {index + 1}</div>
            </button>
          ))}
        </div>

        {/* 선택된 날짜의 활동 */}
        <div className="day-activities">
          <div className="day-header">
            <h2>{travelPlan.days && travelPlan.days[selectedDay] ? formatDate(travelPlan.days[selectedDay].date) : 'Day ' + (selectedDay + 1)}</h2>

            {/* 시작 시간 설정 버튼 - 항상 표시 */}
            <div className="start-time-section">
              {isEditingStartTime ? (
                <div className="start-time-edit" ref={startTimeEditRef}>
                  <input
                    type="time"
                    value={dayStartTime}
                    onChange={(e) => setDayStartTime(e.target.value)}
                    className="time-input-native"
                    autoFocus
                  />
                </div>
              ) : (
                <button
                  className="set-start-time-btn"
                  onClick={() => setIsEditingStartTime(true)}
                >
                  🕘 시작 시각: {dayStartTime}
                </button>
              )}
            </div>
          </div>

          {travelPlan.days && travelPlan.days[selectedDay] && (!travelPlan.days[selectedDay].activities || travelPlan.days[selectedDay].activities.length === 0) &&
          !showAddActivityForm ? (
            <div className="empty-activities">
              <div className="empty-icon">📝</div>
              <p>아직 계획된 활동이 없습니다</p>
              <button
                className="add-first-activity-btn"
                onClick={handleShowAddActivity}
              >
                첫 번째 활동 추가하기
              </button>
            </div>
          ) : (
            <div
              className="activities-timeline"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                // 마지막 insertPosition을 사용하여 drop 처리
                if (insertPositionRef.current !== null) {
                  handleDrop(e, insertPositionRef.current);
                }
              }}
            >
              {(() => {
                // 버퍼 활동을 포함한 활동 목록 생성
                const originalActivities = travelPlan.days && travelPlan.days[selectedDay]
                  ? (travelPlan.days[selectedDay].activities || [])
                  : [];
                const activitiesWithBuffers = insertBufferActivities(originalActivities);

                return activitiesWithBuffers.map((activity, displayIndex) => {
                  // 버퍼 활동의 경우 원본 인덱스를 찾을 수 없으므로 -1
                  const originalIndex = activity.isBuffer
                    ? -1
                    : originalActivities.findIndex(a => a.id === activity.id);

                  return (
                    <InlineActivityCard
                      key={activity.id}
                      activity={activity}
                      index={originalIndex}
                      isEditing={editingActivity === originalIndex && !activity.isBuffer}
                      onEdit={(idx) => !activity.isBuffer && setEditingActivity(idx)}
                      onSave={handleSaveInlineActivity}
                      onCancel={() => setEditingActivity(null)}
                      onDelete={handleDeleteActivity}
                      onToggleHide={handleToggleHideActivity}
                      onDragStart={!activity.isBuffer ? handleDragStart : undefined}
                      onDragOver={!activity.isBuffer ? handleDragOver : undefined}
                      onDrop={!activity.isBuffer ? handleDrop : undefined}
                      onDragEnd={!activity.isBuffer ? handleDragEnd : undefined}
                      onDragLeave={!activity.isBuffer ? handleDragLeave : undefined}
                      categories={categories}
                      showCategoryManagement={showCategoryManagement}
                      setShowCategoryManagement={setShowCategoryManagement}
                      onAddCategory={handleAddCategory}
                      onDeleteCategory={handleDeleteCategory}
                      onChangeCategoryColor={handleChangeCategoryColor}
                      newCategoryName={newCategoryName}
                      setNewCategoryName={setNewCategoryName}
                      newCategoryColor={newCategoryColor}
                      setNewCategoryColor={setNewCategoryColor}
                      travelPlanParticipants={travelPlan?.participants || []}
                      isBuffer={activity.isBuffer}
                    />
                  );
                });
              })()}

              {/* 새 활동 추가 폼 */}
              {showAddActivityForm && (
                <InlineActivityCard
                  isAddMode={true}
                  startTime={getNextStartTime(
                    travelPlan.days && travelPlan.days[selectedDay] ? (travelPlan.days[selectedDay].activities || []) : [],
                    dayStartTime
                  )}
                  onSave={handleAddActivity}
                  onCancel={() => setShowAddActivityForm(false)}
                  categories={categories}
                  showCategoryManagement={showCategoryManagement}
                  setShowCategoryManagement={setShowCategoryManagement}
                  onAddCategory={handleAddCategory}
                  onDeleteCategory={handleDeleteCategory}
                  onChangeCategoryColor={handleChangeCategoryColor}
                  newCategoryName={newCategoryName}
                  setNewCategoryName={setNewCategoryName}
                  newCategoryColor={newCategoryColor}
                  setNewCategoryColor={setNewCategoryColor}
                  travelPlanParticipants={travelPlan?.participants || []}
                />
              )}

              {/* 활동 추가 버튼 */}
              {!showAddActivityForm && (
                <button
                  className="add-activity-btn-bottom"
                  onClick={handleShowAddActivity}
                >
                  + 활동 추가
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 공유 링크 모달 */}
      {showShareModal && (
        <ShareLinkModal
          planId={planId}
          onClose={() => setShowShareModal(false)}
          onSuccess={() => {
            // 공유 링크 생성 성공 시 필요한 동작 (예: 목록 새로고침)
            console.log('공유 링크가 생성되었습니다.');
          }}
        />
      )}

      {/* 공유 링크 목록 모달 */}
      {showShareListModal && (
        <ShareLinkList
          planId={planId}
          onClose={() => setShowShareListModal(false)}
        />
      )}
    </div>
  );
};

export default TravelDetailPage;
